import { Fragment, useEffect, useRef, useState } from "react";
import { api, ApiError } from "./lib/api";
import type {
  CreateRecipeInput,
  Ingredient,
  IngredientContribution,
  MealPlanEntry,
  ParsedIngredientLine,
  ParsedRecipe,
  RecipeDetail,
  RecipeIngredient,
  RecipeInputIngredient,
  RecipeListItem,
  RecipeNutrition,
  RecipeNutritionSummary,
  RecipeStep,
  RecipeSuggestion,
  ShoppingListDetail,
  ShoppingListItem,
  ShoppingListNutrition,
  ShoppingListSummary,
} from "./lib/types";

type View = "recipes" | "add" | "ingredients" | "plan" | "shopping" | "suggest" | "cook";
type Notice = { kind: "ok" | "warn"; text: string } | null;

const COMPUTABLE_UNITS = new Set(["g", "kg", "lb", "oz", "ml", "l", "cup", "tbsp", "tsp"]);

// Generic fat names that need disambiguation before they enter the
// library — "oil" alone could be any of these.
const GENERIC_OIL_NAMES = new Set(["oil", "cooking oil", "neutral oil"]);
const OIL_TYPES = [
  "Olive Oil", "Extra Virgin Olive Oil", "Vegetable Oil", "Canola Oil",
  "Avocado Oil", "Coconut Oil", "Sesame Oil", "Peanut Oil", "Mustard Oil", "Ghee",
];

function errorText(err: unknown) {
  if (err instanceof ApiError) return `${err.status} ${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function fmt(value: number | null | undefined, digits = 0) {
  if (value == null || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: digits }).format(value);
}

function nutritionSummaryStatus(summary: RecipeNutritionSummary) {
  if (summary.is_complete && summary.estimated_count === 0) return "complete";
  const parts: string[] = [];
  if (summary.estimated_count) parts.push(`${summary.estimated_count} est.`);
  if (summary.skipped_count) {
    // all skipped lines benign (optional / to-taste) → say so, it's not an error
    const allOptional = summary.skipped_optional_count === summary.skipped_count;
    parts.push(`${summary.skipped_count}${allOptional ? " optional" : ""} skipped`);
  }
  if (summary.missing_nutrient_fields.length) parts.push("missing fields");
  return parts.join(" · ") || "review";
}

function numOrNull(value: string) {
  if (!value.trim()) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

async function fileToBase64(file: File) {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("could not read file"));
    reader.readAsDataURL(file);
  });
  const match = dataUrl.match(/^data:([^;]+);base64,(.*)$/);
  if (!match) throw new Error("unsupported image file");
  return { mediaType: match[1], base64: match[2] };
}

function ingredientNutritionForm(ingredient: Ingredient) {
  return {
    per_100g_calories: ingredient.per_100g_calories?.toString() ?? "",
    per_100g_protein_g: ingredient.per_100g_protein_g?.toString() ?? "",
    per_100g_carbs_g: ingredient.per_100g_carbs_g?.toString() ?? "",
    per_100g_sugar_g: ingredient.per_100g_sugar_g?.toString() ?? "",
    per_100g_fat_g: ingredient.per_100g_fat_g?.toString() ?? "",
    per_100g_fiber_g: ingredient.per_100g_fiber_g?.toString() ?? "",
    per_100g_sodium_mg: ingredient.per_100g_sodium_mg?.toString() ?? "",
    density_g_per_cup: ingredient.density_g_per_cup?.toString() ?? "",
  };
}

const INGREDIENT_NUTRIENT_FIELDS = [
  { key: "per_100g_calories", label: "Calories / 100g" },
  { key: "per_100g_protein_g", label: "Protein g / 100g" },
  { key: "per_100g_carbs_g", label: "Carbs g / 100g" },
  { key: "per_100g_sugar_g", label: "Sugar g / 100g" },
  { key: "per_100g_fat_g", label: "Fat g / 100g" },
  { key: "per_100g_fiber_g", label: "Fiber g / 100g" },
  { key: "per_100g_sodium_mg", label: "Sodium mg / 100g" },
  { key: "density_g_per_cup", label: "Density g / cup" },
] as const;

/** Short labels for missing-field flags on recipe lines. */
const NUTRIENT_SHORT_LABELS: Record<string, string> = {
  calories: "calories", protein_g: "protein", carbs_g: "carbs", sugar_g: "sugar",
  fat_g: "fat", fiber_g: "fiber", sodium_mg: "sodium",
};

const PROVIDER_LABELS: Record<string, string> = {
  curated: "curated table",
  manual: "curated/manual",
  usda_fdc: "USDA",
  open_food_facts: "Open Food Facts",
  manual_override: "manual override",
};

function fetchOutcomeText(outcome: { status: string; provider?: string; matched_name?: string }): string {
  switch (outcome.status) {
    case "hit":
      return `Matched ${PROVIDER_LABELS[outcome.provider ?? ""] ?? outcome.provider ?? "provider"}` +
        (outcome.matched_name ? ` · "${outcome.matched_name}"` : "") + ". Values saved.";
    case "miss":
      return "No usable match from the curated table, USDA, or Open Food Facts — values unchanged.";
    case "rate_limited":
      return "Provider rate-limited — the nightly retry will pick this up, or try again shortly.";
    default:
      return "Provider error — values unchanged; try again later.";
  }
}

/** Loose same-food candidates for the "similar in library" hint. */
function similarIngredients(name: string, library: Ingredient[]): Ingredient[] {
  const tokens = new Set(
    name.toLowerCase().split(/[^a-z]+/).filter((t) => t.length > 2).map((t) => t.replace(/s$/, "")),
  );
  if (!tokens.size) return [];
  return library
    .filter((i) => i.canonical_name.toLowerCase().split(/[^a-z]+/)
      .some((t) => tokens.has(t.replace(/s$/, ""))))
    .slice(0, 3);
}

function skippedReasonText(reason: string): string {
  switch (reason) {
    case "optional": return "Optional, excluded from totals";
    case "to_taste": return "To taste, excluded from totals";
    case "no_quantity": return "Needs a measured quantity";
    case "no_unit": return "Needs a unit";
    case "unknown_unit": return "Unknown unit; add a household unit";
    case "density_unknown": return "Needs density for volume conversion";
    case "incompatible_units": return "Needs a household unit conversion";
    case "no_nutrition_data": return "Needs nutrition data";
    default: return reason.replace(/_/g, " ");
  }
}

function Header({ view, setView }: { view: View; setView: (view: View) => void }) {
  const nav: { id: View; label: string }[] = [
    { id: "recipes", label: "recipes" },
    { id: "add", label: "add recipe" },
    { id: "ingredients", label: "ingredients" },
    { id: "plan", label: "meal plan" },
    { id: "shopping", label: "shopping" },
    { id: "suggest", label: "what can i make" },
    { id: "cook", label: "cook" },
  ];

  return (
    <header className="border-b border-bg4 bg-bg/75 backdrop-blur">
      <div className="mx-auto flex max-w-[1500px] items-center justify-between px-4 py-4 sm:px-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.28em] text-accent">Recipe Library</div>
          <h1 className="text-2xl font-extrabold tracking-tight text-fg">Cookbook Control Center</h1>
        </div>
        <nav className="flex flex-wrap justify-end gap-2">
          {nav.map((item) => (
            <button key={item.id} className={`btn ${view === item.id ? "border-accent text-accent" : ""}`} onClick={() => setView(item.id)}>
              {item.label}
            </button>
          ))}
          <button
            className="btn"
            title="Set API key (sent as x-api-key; only needed once the Worker's API_KEY secret is enabled)"
            onClick={() => {
              const current = localStorage.getItem("rl_api_key") ?? "";
              const next = window.prompt("API key (leave empty to clear):", current);
              if (next == null) return;
              if (next.trim()) localStorage.setItem("rl_api_key", next.trim());
              else localStorage.removeItem("rl_api_key");
              window.location.reload();
            }}
          >
            🔑
          </button>
        </nav>
      </div>
    </header>
  );
}

function NoticeBar({ notice }: { notice: Notice }) {
  if (!notice) return null;
  return (
    <div className={`rounded-2xl border px-4 py-3 text-sm ${notice.kind === "ok" ? "border-herb/40 bg-herb/10 text-accent2" : "border-warn/40 bg-warn/10 text-warn"}`}>
      {notice.text}
    </div>
  );
}

function RecipeList({
  selectedId,
  onSelect,
  refreshToken,
  onBulkDeleted,
}: {
  selectedId: number | null;
  onSelect: (id: number) => void;
  refreshToken: number;
  onBulkDeleted: (deletedIds: number[]) => void;
}) {
  const [search, setSearch] = useState("");
  const [favoriteOnly, setFavoriteOnly] = useState(false);
  const [recipes, setRecipes] = useState<RecipeListItem[]>([]);
  const [summaryById, setSummaryById] = useState<Record<number, RecipeNutritionSummary>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [tagFilters, setTagFilters] = useState<Set<string>>(new Set());

  useEffect(() => {
    api.listRecipeTags()
      .then((tags) => setAvailableTags([...new Set(tags.map((t) => t.tag_value))].sort()))
      .catch(() => undefined);
  }, [refreshToken]);

  function toggleTagFilter(tag: string) {
    setTagFilters((current) => {
      const next = new Set(current);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }

  function toggleChecked(id: number, checked: boolean) {
    setCheckedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function deleteChecked() {
    const ids = [...checkedIds];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} recipe(s)? Ingredients stay in the library.`)) return;
    const results = await Promise.allSettled(ids.map((id) => api.deleteRecipe(id)));
    const deleted = ids.filter((_, i) => results[i].status === "fulfilled");
    const failed = ids.length - deleted.length;
    setCheckedIds(new Set());
    setError(failed ? `Deleted ${deleted.length}; ${failed} delete(s) failed.` : null);
    onBulkDeleted(deleted);
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.listRecipes({ search, favorite: favoriteOnly ? true : undefined, tags: [...tagFilters] })
      .then((data) => {
        if (!cancelled) {
          setRecipes(data);
          setSummaryById({});
          setError(null);
          if (!selectedId && data[0]) onSelect(data[0].id);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(errorText(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [favoriteOnly, onSelect, refreshToken, search, selectedId, tagFilters]);

  useEffect(() => {
    if (!recipes.length) return;
    let cancelled = false;
    api.getRecipeNutritionSummaries(recipes.map((recipe) => recipe.id))
      .then((summaries) => {
        if (!cancelled) {
          setSummaryById(Object.fromEntries(summaries.map((summary) => [summary.recipe_id, summary])));
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [recipes]);

  return (
    <section className="panel overflow-hidden">
      <div className="border-b border-bg4 p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-lg font-bold">Recipes</h2>
          <div className="flex items-center gap-2">
            {checkedIds.size > 0 && (
              <button
                className="btn border-danger/50 text-danger hover:border-danger hover:text-danger"
                onClick={() => void deleteChecked()}
              >
                delete {checkedIds.size} selected
              </button>
            )}
            <span className="pill">{recipes.length} shown</span>
          </div>
        </div>
        <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
          <input className="field" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search recipes…" />
          <button className={`btn ${favoriteOnly ? "border-accent text-accent" : ""}`} onClick={() => setFavoriteOnly((v) => !v)}>
            ★ favorites
          </button>
        </div>
        {availableTags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {availableTags.map((tag) => (
              <button
                key={tag}
                className={`pill transition ${tagFilters.has(tag) ? "border-accent bg-accent/10 text-accent" : "hover:border-accent/60"}`}
                onClick={() => toggleTagFilter(tag)}
              >
                {tag}
              </button>
            ))}
            {tagFilters.size > 0 && (
              <button className="pill text-muted hover:text-fg" onClick={() => setTagFilters(new Set())}>clear ✕</button>
            )}
          </div>
        )}
      </div>
      <div className="max-h-[70vh] overflow-auto p-2">
        {loading && <div className="p-3 text-sm text-muted">Loading recipes…</div>}
        {error && <div className="p-3 text-sm text-danger">{error}</div>}
        {!loading && !recipes.length && <div className="p-3 text-sm text-muted">No recipes yet.</div>}
        {recipes.map((recipe) => {
          const summary = summaryById[recipe.id];
          return (
            <div
              key={recipe.id}
              onClick={() => onSelect(recipe.id)}
              className={`mb-2 w-full cursor-pointer rounded-xl border p-3 text-left transition ${selectedId === recipe.id ? "border-accent bg-accent/10" : "border-bg4 bg-bg hover:border-accent/60"}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={checkedIds.has(recipe.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => toggleChecked(recipe.id, e.target.checked)}
                  />
                  <div className="font-semibold text-fg">{recipe.name}</div>
                </div>
                <div className={recipe.favorite ? "text-accent" : "text-muted"}>★</div>
              </div>
              <div className="mt-1 pl-6 text-xs text-muted">
                serves {fmt(recipe.servings, 1)} · v{recipe.version}
                {recipe.prep_min != null ? ` · prep ${recipe.prep_min}m` : ""}
                {recipe.cook_min != null ? ` · cook ${recipe.cook_min}m` : ""}
              </div>
              {summary && (
                <div className="mt-2 flex flex-wrap gap-1.5 pl-6 text-xs">
                  <span className="pill border-herb/35 text-accent2">{fmt(summary.calories)} cal</span>
                  <span className="pill border-herb/35 text-accent2">{fmt(summary.protein_g, 1)}g protein</span>
                  <span className={`pill ${summary.is_complete && summary.estimated_count === 0 ? "border-herb/35 text-accent2" : "border-warn/40 text-warn"}`}>
                    {nutritionSummaryStatus(summary)}
                  </span>
                </div>
              )}
              {recipe.tags_summary && (
                <div className="mt-1 pl-6 text-xs text-accent/80">{recipe.tags_summary}</div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function RecipeDetailPanel({
  id,
  onChanged,
  onDeleted,
  onCook,
  onOpenIngredient,
}: {
  id: number | null;
  onChanged: () => void;
  onDeleted: () => void;
  onCook: (id: number) => void;
  onOpenIngredient: (ingredientId: number) => void;
}) {
  const [detail, setDetail] = useState<RecipeDetail | null>(null);
  const [nutrition, setNutrition] = useState<RecipeNutrition | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchingNutrition, setFetchingNutrition] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [cookFor, setCookFor] = useState("");
  const [expandedAudit, setExpandedAudit] = useState<Set<number>>(new Set());

  function toggleAudit(idx: number) {
    setExpandedAudit((current) => {
      const next = new Set(current);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  async function reload(recipeId: number) {
    const [recipe, nut] = await Promise.all([api.getRecipe(recipeId), api.getRecipeNutrition(recipeId)]);
    setDetail(recipe);
    setNutrition(nut);
    setCookFor(String(recipe.servings));
    setExpandedAudit(new Set());
    setError(null);
  }

  async function chooseAlternative(choicePosition: number) {
    if (!detail) return;
    const choice = detail.ingredients.find((item) => item.position === choicePosition);
    if (!choice) return;
    const groupPosition = choice.alt_of ?? choice.position;
    try {
      await api.patchRecipe(detail.id, {
        ingredients: detail.ingredients.map((item, idx) => {
          const inGroup = item.position === groupPosition || item.alt_of === groupPosition;
          return {
            position: idx + 1,
            ingredient_id: item.ingredient_id,
            quantity: item.quantity,
            quantity_min: item.quantity_min,
            quantity_max: item.quantity_max,
            quantity_text: item.quantity_text,
            unit: item.unit,
            section: item.section,
            prep_note: item.prep_note,
            is_optional: item.is_optional,
            selected: inGroup ? item.position === choicePosition : item.selected,
            alt_of: item.alt_of,
          };
        }),
      });
      await reload(detail.id);
      onChanged();
    } catch (err) {
      setError(errorText(err));
    }
  }

  // Ignore a line that isn't really an ingredient ("Add After Cooking",
  // section headers): drop it from the recipe and add it to the global
  // ignore list so future imports skip it too.
  async function ignoreIngredient(item: RecipeIngredient) {
    if (!detail) return;
    if (!window.confirm(`Ignore "${item.ingredient_name}"? It will be removed from this recipe and skipped on future imports.`)) return;
    try {
      await api.patchRecipe(detail.id, {
        ingredients: detail.ingredients
          .filter((i) => i.position !== item.position)
          .map((i, idx) => ({
            position: idx + 1,
            ingredient_id: i.ingredient_id,
            quantity: i.quantity,
            quantity_min: i.quantity_min,
            quantity_max: i.quantity_max,
            quantity_text: i.quantity_text,
            unit: i.unit,
            section: i.section,
            prep_note: i.prep_note,
            is_optional: i.is_optional,
            selected: i.selected,
            // A removed primary would orphan its alternatives; clear the link.
            alt_of: i.alt_of === item.position ? null : i.alt_of,
          })),
      });
      await api.learnNonIngredient(item.ingredient_name).catch(() => {});
      await reload(detail.id);
      onChanged();
    } catch (err) {
      setError(errorText(err));
    }
  }

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setEditing(false);
    reload(id)
      .catch((err: unknown) => {
        if (!cancelled) setError(errorText(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function toggleFavorite() {
    if (!detail) return;
    await api.patchRecipe(detail.id, { favorite: !detail.favorite });
    onChanged();
    const updated = await api.getRecipe(detail.id);
    setDetail(updated);
  }

  async function deleteRecipe() {
    if (!detail) return;
    const ok = window.confirm(`Delete recipe "${detail.name}"? Ingredients stay in the library.`);
    if (!ok) return;
    await api.deleteRecipe(detail.id);
    setDetail(null);
    setNutrition(null);
    onDeleted();
  }

  async function fetchRecipeNutrition() {
    if (!detail) return;
    setFetchingNutrition(true);
    try {
      // Only fetch ingredients that actually need data — refetching a
      // verified row risks replacing good values if a provider regresses.
      // Estimated-density lines are included because a curated match can
      // supply a real density. Force-refreshing a single verified
      // ingredient lives on its library page.
      const targets = new Set<number>();
      for (const c of nutrition?.computed.per_ingredient ?? []) {
        if (c.skipped === "no_nutrition_data" || c.missing_fields.length > 0 || c.estimated) {
          targets.add(c.ingredient_id);
        }
      }
      const ids = [...targets];
      for (let i = 0; i < ids.length; i += 3) {
        await Promise.allSettled(ids.slice(i, i + 3).map((ingredientId) => api.fetchIngredient(ingredientId)));
      }
      await reload(detail.id);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setFetchingNutrition(false);
    }
  }

  if (!id) {
    return <section className="panel p-8 text-muted">Choose a recipe or add a new one.</section>;
  }
  if (loading && !detail) return <section className="panel p-8 text-muted">Loading recipe…</section>;
  if (error) return <section className="panel p-8 text-danger">{error}</section>;
  if (!detail) return null;

  const skipped = nutrition?.computed.ingredients_skipped ?? [];
  // Scale factor for the "cook for N" control (1 = as written).
  const cookForN = Number(cookFor);
  const scale = Number.isFinite(cookForN) && cookForN > 0 ? cookForN / detail.servings : 1;
  // Per-line contributions, aligned to selected ingredient rows. An
  // ingredient can appear on several lines — consume each id's
  // contributions as a queue, in row order.
  const contributionQueues = new Map<number, IngredientContribution[]>();
  for (const c of nutrition?.computed.per_ingredient ?? []) {
    const q = contributionQueues.get(c.ingredient_id) ?? [];
    q.push(c);
    contributionQueues.set(c.ingredient_id, q);
  }
  const rowContributions = detail.ingredients.map((item) =>
    item.selected ? contributionQueues.get(item.ingredient_id)?.shift() ?? null : null,
  );
  return (
    <section className="panel overflow-hidden">
      <div className="border-b border-bg4 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="label">recipe detail</div>
            <h2 className="mt-1 text-3xl font-extrabold tracking-tight">{detail.name}</h2>
            <p className="mt-2 text-sm text-muted">
              Serves {fmt(detail.servings, 1)} · version {detail.version}
              {detail.prep_min != null ? ` · prep ${detail.prep_min}m` : ""}
              {detail.cook_min != null ? ` · cook ${detail.cook_min}m` : ""}
            </p>
          </div>
          <div className="flex gap-2">
            <button className={`btn ${editing ? "border-accent text-accent" : ""}`} onClick={() => setEditing((v) => !v)}>
              {editing ? "cancel edit" : "edit"}
            </button>
            <button className="btn" onClick={() => onCook(detail.id)}>cook view</button>
            <button className="btn" onClick={() => void toggleFavorite()}>
              {detail.favorite ? "★ favorite" : "☆ mark favorite"}
            </button>
            <button className="btn border-danger/50 text-danger hover:border-danger hover:text-danger" onClick={() => void deleteRecipe()}>
              delete
            </button>
          </div>
        </div>
        {(detail.meal_slots.length > 0 || detail.tags.length > 0) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {detail.meal_slots.map((slot) => <span key={slot} className="pill border-accent/40 text-accent">{slot}</span>)}
            {detail.tags.map((tag) => (
              <span key={`${tag.tag_category}-${tag.tag_value}`} className="pill">{tag.tag_category}: {tag.tag_value}</span>
            ))}
          </div>
        )}
        {detail.description && <p className="mt-4 max-w-3xl text-sm leading-6 text-muted">{detail.description}</p>}
      </div>

      {editing ? (
        <RecipeEditForm
          detail={detail}
          onCancel={() => setEditing(false)}
          onSaved={async () => {
            await reload(detail.id);
            setEditing(false);
            onChanged();
          }}
        />
      ) : (
      <div className="grid gap-4 p-5 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-4">
          <Block title="Ingredients">
            <div className="mb-3 flex items-center gap-2 text-sm text-muted">
              <span>cook for</span>
              <input
                className="field w-20 py-1 text-center"
                value={cookFor}
                onChange={(e) => setCookFor(e.target.value)}
              />
              <span>servings{scale !== 1 ? ` — quantities scaled ×${fmt(scale, 2)}` : " (as written)"}</span>
            </div>
            <div className="space-y-2">
              {detail.ingredients.map((item, idx) => {
                const contribution = rowContributions[idx];
                const previousSection = idx > 0 ? detail.ingredients[idx - 1].section : null;
                const showSection = item.section && item.section !== previousSection;
                const isAlternativeGroup =
                  item.alt_of != null || detail.ingredients.some((candidate) => candidate.alt_of === item.position);
                return (
                  <Fragment key={`${item.position}-${item.ingredient_id}`}>
                    {showSection && <div className="label pt-2 text-accent">{item.section}</div>}
                    <div
                      onClick={() => onOpenIngredient(item.ingredient_id)}
                      title="Open in ingredient library"
                      className={`cursor-pointer rounded-xl border border-bg4 bg-bg p-3 transition hover:border-accent/60 ${!item.selected ? "opacity-60" : ""}`}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-fg">{item.alt_of != null ? `or ${item.ingredient_name}` : item.ingredient_name}</span>
                        {isAlternativeGroup && (
                          item.selected ? (
                            <span className="pill border-herb/40 text-accent2">selected</span>
                          ) : (
                            <button
                              className="pill border-accent/40 text-accent hover:bg-accent/10"
                              onClick={(e) => { e.stopPropagation(); void chooseAlternative(item.position); }}
                            >
                              use this
                            </button>
                          )
                        )}
                        {item.is_optional && <span className="pill border-warn/40 text-warn">optional</span>}
                        <button
                          className="pill ml-auto border-bg4 text-muted hover:border-warn hover:text-warn"
                          title="Not an ingredient — remove it and skip it on future imports"
                          onClick={(e) => { e.stopPropagation(); void ignoreIngredient(item); }}
                        >
                          ignore
                        </button>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-sm text-muted">
                        <span>
                          {scale === 1
                            ? (item.quantity_text ?? `${fmt(item.quantity, 2)} ${item.unit ?? ""}`.trim())
                            : item.quantity != null
                              ? `${fmt(item.quantity * scale, 2)} ${item.unit ?? ""}`.trim()
                              : (item.quantity_text ?? "")}
                          {item.prep_note ? ` · ${item.prep_note}` : ""}
                        </span>
                        {!item.selected ? (
                          <span className="text-xs">not counted (alternative)</span>
                        ) : contribution?.skipped ? (
                          <span className="pill border-warn/40 text-warn">{skippedReasonText(contribution.skipped)}</span>
                        ) : contribution ? (
                          <span className="font-mono text-xs">
                            {fmt((contribution.calories ?? 0) * scale, 0)} cal · {fmt((contribution.protein_g ?? 0) * scale, 1)}g protein
                            {contribution.estimated ? " · est." : ""}
                            {contribution.missing_fields.length > 0 && (
                              <span className="text-warn" title="Unknown on the ingredient record — click through and fill it in or hit fetch.">
                                {" "}· unknown: {contribution.missing_fields.map((f) => NUTRIENT_SHORT_LABELS[f] ?? f).join(", ")}
                              </span>
                            )}
                            <button
                              className="ml-2 text-muted underline decoration-dotted hover:text-fg"
                              onClick={(e) => { e.stopPropagation(); toggleAudit(idx); }}
                            >
                              {expandedAudit.has(idx) ? "hide" : "details"}
                            </button>
                          </span>
                        ) : null}
                      </div>
                      {expandedAudit.has(idx) && contribution && contribution.skipped == null && (
                        <div
                          className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 rounded-lg border border-bg4 p-2 font-mono text-xs text-muted sm:grid-cols-4"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span>{fmt((contribution.grams ?? 0) * scale, 0)} g used</span>
                          <span>cal {contribution.calories != null ? fmt(contribution.calories * scale, 0) : "—"}</span>
                          <span>protein {contribution.protein_g != null ? fmt(contribution.protein_g * scale, 1) : "—"}g</span>
                          <span>carbs {contribution.carbs_g != null ? fmt(contribution.carbs_g * scale, 1) : "—"}g</span>
                          <span>sugar {contribution.sugar_g != null ? fmt(contribution.sugar_g * scale, 1) : "—"}g</span>
                          <span>fat {contribution.fat_g != null ? fmt(contribution.fat_g * scale, 1) : "—"}g</span>
                          <span>fiber {contribution.fiber_g != null ? fmt(contribution.fiber_g * scale, 1) : "—"}g</span>
                          <span>sodium {contribution.sodium_mg != null ? fmt(contribution.sodium_mg * scale, 0) : "—"}mg</span>
                          <span className="col-span-2 font-sans sm:col-span-4">
                            source: {PROVIDER_LABELS[contribution.nutrition_source ?? ""] ?? contribution.nutrition_source ?? "—"}
                            {" "}· confidence: {contribution.confidence ?? "—"}
                            {contribution.estimated ? " · grams via estimated density" : ""}
                          </span>
                        </div>
                      )}
                    </div>
                  </Fragment>
                );
              })}
            </div>
          </Block>
          <Block title="Steps">
            <div className="space-y-3">
              {detail.steps.map((step) => (
                <div key={step.position} className="rounded-xl border border-bg4 bg-bg p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold">{step.position}. {step.title ?? "Step"}</div>
                    {step.timer_minutes != null && <span className="pill">{step.timer_minutes} min</span>}
                  </div>
                  <p className="mt-2 whitespace-pre-line text-sm leading-6 text-muted">{step.text}</p>
                </div>
              ))}
            </div>
          </Block>
        </div>

        <div className="space-y-4">
          <Block title={`Computed nutrition — per serving (serves ${fmt(detail.servings, 1)})`}>
            {nutrition ? (
              <div className="space-y-4">
                <p className="text-xs text-muted">
                  All values below are for ONE serving; the recipe as written makes {fmt(detail.servings, 1)}.
                  Per-line amounts on the left are for the whole batch.
                </p>
                <button className="btn w-full justify-center" disabled={fetchingNutrition} onClick={() => void fetchRecipeNutrition()}>
                  {fetchingNutrition ? "fetching ingredients…" : "fetch nutrition for recipe"}
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <Nutrient label="cal / serving" value={nutrition.computed.per_serving.calories} />
                  <Nutrient label="protein" value={nutrition.computed.per_serving.protein_g} unit="g" />
                  <Nutrient label="carbs" value={nutrition.computed.per_serving.carbs_g} unit="g" />
                  <Nutrient label="fat" value={nutrition.computed.per_serving.fat_g} unit="g" />
                </div>
                <div className={`rounded-xl border p-3 text-sm ${nutrition.computed.is_complete ? "border-herb/40 bg-herb/10 text-accent2" : "border-warn/40 bg-warn/10 text-warn"}`}>
                  {!nutrition.computed.is_complete
                    ? `${skipped.length} ingredient(s) skipped or incomplete`
                    : nutrition.computed.estimated_count === 0 && nutrition.computed.missing_nutrient_fields.length === 0
                      ? "Complete nutrition inputs"
                      : `Computable${nutrition.computed.estimated_count > 0 ? ` — ${nutrition.computed.estimated_count} line(s) use estimated densities` : ""}${nutrition.computed.missing_nutrient_fields.length > 0 ? `; not counted: ${nutrition.computed.missing_nutrient_fields.map((f) => NUTRIENT_SHORT_LABELS[f] ?? f).join(", ")}` : ""}`}
                </div>
                {skipped.length > 0 && (
                  <div className="space-y-1 text-sm text-muted">
                    {skipped.map((item) => (
                      <div key={`${item.ingredient_id}-${item.reason}`}>
                        <span className="font-semibold text-fg">{item.ingredient_name}</span>: {skippedReasonText(item.reason)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted">Nutrition unavailable.</p>
            )}
          </Block>
          <Block title="Override">
            {nutrition?.override ? (
              <div className="space-y-2 text-sm text-muted">
                <div className="text-fg">{fmt(nutrition.override.calories, 0)} calories · {fmt(nutrition.override.protein_g, 1)}g protein</div>
                <div>source: {nutrition.override.source ?? "manual"} · based on v{nutrition.override.based_on_recipe_version}</div>
                {nutrition.override.needs_review === 1 && <span className="pill border-warn/40 text-warn">needs review</span>}
              </div>
            ) : (
              <p className="text-sm text-muted">No manual override.</p>
            )}
          </Block>
        </div>
      </div>
      )}
    </section>
  );
}

const MEAL_SLOTS = ["breakfast", "lunch", "dinner", "snack"] as const;
const TAG_CATEGORIES = ["cuisine", "method", "dietary"] as const;

interface EditIngredientRow {
  origPosition: number | null;
  ingredientId: string;
  quantity: string;
  unit: string;
  section: string;
  prepNote: string;
  optional: boolean;
  selected: boolean;
  altOf: number | null;
  quantityText: string | null;
}

interface EditStepRow {
  title: string;
  text: string;
  timerMinutes: number | null;
}

function RecipeEditForm({
  detail,
  onCancel,
  onSaved,
}: {
  detail: RecipeDetail;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [library, setLibrary] = useState<Ingredient[]>([]);
  const [name, setName] = useState(detail.name);
  const [servings, setServings] = useState(String(detail.servings));
  const [prepMin, setPrepMin] = useState(detail.prep_min?.toString() ?? "");
  const [cookMin, setCookMin] = useState(detail.cook_min?.toString() ?? "");
  const [description, setDescription] = useState(detail.description ?? "");
  const [mealSlots, setMealSlots] = useState<string[]>(detail.meal_slots);
  const [tags, setTags] = useState(detail.tags);
  const [tagCategory, setTagCategory] = useState<string>("cuisine");
  const [tagValue, setTagValue] = useState("");
  const [rows, setRows] = useState<EditIngredientRow[]>(
    detail.ingredients.map((item) => ({
      origPosition: item.position,
      ingredientId: String(item.ingredient_id),
      quantity: item.quantity?.toString() ?? "",
      unit: item.unit ?? "",
      section: item.section ?? "",
      prepNote: item.prep_note ?? "",
      optional: item.is_optional,
      selected: item.selected,
      altOf: item.alt_of,
      quantityText: item.quantity_text,
    })),
  );
  const [steps, setSteps] = useState<EditStepRow[]>(
    detail.steps.map((s) => ({ title: s.title ?? "", text: s.text, timerMinutes: s.timer_minutes })),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.listIngredients({ limit: 1000 }).then(setLibrary).catch((err: unknown) => setError(errorText(err)));
  }, []);

  function updateRow(idx: number, patch: Partial<EditIngredientRow>) {
    setRows((current) => current.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  }

  function updateStep(idx: number, patch: Partial<EditStepRow>) {
    setSteps((current) => current.map((step, i) => (i === idx ? { ...step, ...patch } : step)));
  }

  async function save() {
    const kept = rows.filter((row) => row.ingredientId);
    // alt_of references the PRIMARY row's position; rows may have moved or
    // been deleted, so remap original positions to the new ones.
    const newPositionByOrig = new Map<number, number>();
    kept.forEach((row, idx) => {
      if (row.origPosition != null) newPositionByOrig.set(row.origPosition, idx + 1);
    });
    setSaving(true);
    try {
      await api.patchRecipe(detail.id, {
        name: name.trim(),
        servings: Number(servings),
        prep_min: prepMin.trim() ? Number(prepMin) : null,
        cook_min: cookMin.trim() ? Number(cookMin) : null,
        description: description.trim() || null,
        meal_slots: mealSlots,
        tags,
        ingredients: kept.map((row, idx) => ({
          position: idx + 1,
          ingredient_id: Number(row.ingredientId),
          quantity: numOrNull(row.quantity),
          // Quantity text only survives when the number wasn't edited.
          quantity_text: row.quantityText && numOrNull(row.quantity) === (detail.ingredients.find((i) => i.position === row.origPosition)?.quantity ?? null) ? row.quantityText : null,
          unit: row.unit.trim() || null,
          section: row.section.trim() || null,
          prep_note: row.prepNote.trim() || null,
          is_optional: row.optional,
          selected: row.selected,
          alt_of: row.altOf != null ? newPositionByOrig.get(row.altOf) ?? null : null,
        })),
        steps: steps
          .filter((s) => s.text.trim())
          .map((s, idx) => ({
            position: idx + 1,
            title: s.title.trim() || null,
            text: s.text.trim(),
            timer_minutes: s.timerMinutes,
          })),
      });
      await onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5 p-5">
      {error && <div className="rounded-xl border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{error}</div>}
      <div className="grid gap-3 md:grid-cols-4">
        <label className="space-y-1 md:col-span-2">
          <span className="label">name</span>
          <input className="field" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="space-y-1">
          <span className="label">servings</span>
          <input className="field" value={servings} onChange={(e) => setServings(e.target.value)} />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="space-y-1">
            <span className="label">prep min</span>
            <input className="field" value={prepMin} onChange={(e) => setPrepMin(e.target.value)} />
          </label>
          <label className="space-y-1">
            <span className="label">cook min</span>
            <input className="field" value={cookMin} onChange={(e) => setCookMin(e.target.value)} />
          </label>
        </div>
      </div>
      <label className="block space-y-1">
        <span className="label">description</span>
        <textarea className="field min-h-20" value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>

      <div className="flex flex-wrap items-center gap-4">
        <span className="label">meal slots</span>
        {MEAL_SLOTS.map((slot) => (
          <label key={slot} className="flex items-center gap-1 text-sm text-muted">
            <input
              type="checkbox"
              checked={mealSlots.includes(slot)}
              onChange={(e) => setMealSlots((current) => e.target.checked ? [...current, slot] : current.filter((s) => s !== slot))}
            />
            {slot}
          </label>
        ))}
      </div>

      <div>
        <div className="label mb-2">tags</div>
        <div className="mb-2 flex flex-wrap gap-2">
          {tags.map((tag) => (
            <button
              key={`${tag.tag_category}-${tag.tag_value}`}
              className="pill hover:border-danger hover:text-danger"
              title="Remove tag"
              onClick={() => setTags((current) => current.filter((t) => !(t.tag_category === tag.tag_category && t.tag_value === tag.tag_value)))}
            >
              {tag.tag_category}: {tag.tag_value} ✕
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <select className="field w-36" value={tagCategory} onChange={(e) => setTagCategory(e.target.value)}>
            {TAG_CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
          </select>
          <input className="field w-48" value={tagValue} onChange={(e) => setTagValue(e.target.value)} placeholder="e.g. indian" />
          <button
            className="btn"
            disabled={!tagValue.trim() || tags.length >= 5}
            onClick={() => {
              const value = tagValue.trim().toLowerCase();
              setTags((current) => current.some((t) => t.tag_category === tagCategory && t.tag_value === value)
                ? current
                : [...current, { tag_category: tagCategory, tag_value: value }]);
              setTagValue("");
            }}
          >
            add tag
          </button>
          <span className="self-center text-xs text-muted">{tags.length}/5</span>
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <div className="label">ingredients</div>
          <button
            className="btn"
            onClick={() => setRows((current) => [...current, {
              origPosition: null, ingredientId: "", quantity: "", unit: "g", section: "",
              prepNote: "", optional: false, selected: true, altOf: null, quantityText: null,
            }])}
          >
            add ingredient
          </button>
        </div>
        <div className="space-y-2">
          {rows.map((row, idx) => (
            <div key={idx} className="grid gap-2 rounded-2xl border border-bg4 bg-bg p-3 md:grid-cols-[1fr_6rem_6rem_8rem_1fr_auto]">
              <select className="field" value={row.ingredientId} onChange={(e) => updateRow(idx, { ingredientId: e.target.value })}>
                <option value="">— remove this line —</option>
                {library.map((ing) => <option key={ing.id} value={ing.id}>{ing.canonical_name}</option>)}
              </select>
              <input className="field" value={row.quantity} onChange={(e) => updateRow(idx, { quantity: e.target.value })} placeholder="qty" />
              <input className="field" value={row.unit} onChange={(e) => updateRow(idx, { unit: e.target.value })} placeholder="unit" />
              <input className="field" value={row.section} onChange={(e) => updateRow(idx, { section: e.target.value })} placeholder="section" />
              <input className="field" value={row.prepNote} onChange={(e) => updateRow(idx, { prepNote: e.target.value })} placeholder="prep note" />
              <label className="flex items-center gap-2 text-sm text-muted">
                <input type="checkbox" checked={row.optional} onChange={(e) => updateRow(idx, { optional: e.target.checked })} />
                optional
              </label>
              <label className="flex items-center gap-2 text-sm text-muted md:col-span-6">
                <input type="checkbox" checked={row.selected} onChange={(e) => updateRow(idx, { selected: e.target.checked })} />
                count this row for nutrition/shopping
              </label>
              {row.altOf != null && <div className="text-xs text-accent md:col-span-6">alternative — only one of the group should be selected</div>}
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <div className="label">steps</div>
          <button className="btn" onClick={() => setSteps((current) => [...current, { title: "", text: "", timerMinutes: null }])}>
            add step
          </button>
        </div>
        <div className="space-y-2">
          {steps.map((step, idx) => (
            <div key={idx} className="grid gap-2 rounded-2xl border border-bg4 bg-bg p-3">
              <div className="grid gap-2 md:grid-cols-[1fr_auto]">
                <input className="field" value={step.title} onChange={(e) => updateStep(idx, { title: e.target.value })} placeholder={`Step ${idx + 1} title (optional)`} />
                <button className="btn border-danger/50 text-danger" onClick={() => setSteps((current) => current.filter((_, i) => i !== idx))}>remove</button>
              </div>
              <textarea className="field min-h-20" value={step.text} onChange={(e) => updateStep(idx, { text: e.target.value })} placeholder="Step text (empty = dropped on save)" />
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <button className="btn btn-primary" disabled={saving || !name.trim() || !Number(servings)} onClick={() => void save()}>
          {saving ? "saving…" : "save changes"}
        </button>
        <button className="btn" onClick={onCancel}>cancel</button>
      </div>
      <p className="text-xs text-muted">
        Saving replaces the recipe's ingredients and steps; nutrition and shopping lists recompute automatically on next view.
        Changing quantities, ingredients, or servings bumps the recipe version.
      </p>
    </div>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="label mb-2">{title}</div>
      {children}
    </div>
  );
}

function Nutrient({ label, value, unit = "" }: { label: string; value: number; unit?: string }) {
  return (
    <div className="rounded-xl border border-bg4 bg-bg p-3">
      <div className="text-xs uppercase tracking-wider text-muted">{label}</div>
      <div className="mt-1 font-mono text-xl font-bold text-fg">{fmt(value, 1)}{unit}</div>
    </div>
  );
}

function AddRecipeScreen({ onCreated }: { onCreated: (id: number) => void }) {
  const [mode, setMode] = useState<"paste" | "structured">("paste");
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [notice, setNotice] = useState<Notice>(null);

  useEffect(() => {
    api.listIngredients({ limit: 1000 }).then(setIngredients).catch((err: unknown) => setNotice({ kind: "warn", text: errorText(err) }));
  }, []);

  return (
    <section className="panel p-5">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="label">add recipe</div>
          <h2 className="mt-1 text-3xl font-extrabold">Two ways in</h2>
          <p className="mt-2 max-w-3xl text-sm text-muted">
            Paste a recipe for side-by-side review, or build a structured recipe directly. Unmatched ingredients are either mapped to the library or explicitly created as needs_review before save.
          </p>
        </div>
        <div className="flex rounded-xl border border-bg4 bg-bg p-1">
          <button className={`rounded-lg px-3 py-2 text-sm font-semibold ${mode === "paste" ? "bg-accent text-bg" : "text-muted"}`} onClick={() => setMode("paste")}>smart paste</button>
          <button className={`rounded-lg px-3 py-2 text-sm font-semibold ${mode === "structured" ? "bg-accent text-bg" : "text-muted"}`} onClick={() => setMode("structured")}>structured</button>
        </div>
      </div>
      <NoticeBar notice={notice} />
      <div className="mt-5">
        {mode === "paste" ? (
          <SmartPasteFlow ingredients={ingredients} onCreated={onCreated} setIngredients={setIngredients} setNotice={setNotice} />
        ) : (
          <StructuredRecipeForm ingredients={ingredients} onCreated={onCreated} setNotice={setNotice} />
        )}
      </div>
    </section>
  );
}

interface ParsedDecision {
  ingredientId: string;
  acceptNew: boolean;
  useAlt: boolean;
  /** For generic "oil" lines: the concrete oil the operator picked. */
  oilChoice: string;
  /** Inline corrections — initialized from the parse, editable in review. */
  nameText: string;
  quantity: string;
  unit: string;
}

function defaultDecision(line: ParsedIngredientLine): ParsedDecision {
  return {
    ingredientId: line.matched_ingredient_id ? String(line.matched_ingredient_id) : "",
    acceptNew: false,
    useAlt: Boolean(line.alt_quantity != null && (!line.unit || !COMPUTABLE_UNITS.has(line.unit))),
    oilChoice: "",
    nameText: line.name_text,
    quantity: line.quantity?.toString() ?? "",
    unit: line.unit ?? "",
  };
}

function SmartPasteFlow({
  ingredients,
  setIngredients,
  onCreated,
  setNotice,
}: {
  ingredients: Ingredient[];
  setIngredients: (ingredients: Ingredient[]) => void;
  onCreated: (id: number) => void;
  setNotice: (notice: Notice) => void;
}) {
  const [text, setText] = useState("");
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [importingPhoto, setImportingPhoto] = useState(false);
  const [parsed, setParsed] = useState<ParsedRecipe | null>(null);
  const [decisions, setDecisions] = useState<Record<number, ParsedDecision>>({});
  const [discarded, setDiscarded] = useState<Set<number>>(new Set());
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function adoptDraft(draft: ParsedRecipe, how: string) {
    setParsed(draft);
    setDecisions(Object.fromEntries(draft.ingredients.map((line, idx) => [idx, defaultDecision(line)])));
    setDiscarded(new Set());
    setNotice({ kind: "ok", text: `${how} ${draft.ingredients.length} ingredients and ${draft.steps.length} steps.` });
  }

  async function parse(t: string = text) {
    if (!t.trim()) return;
    setParsing(true);
    try {
      adoptDraft(await api.parseRecipe(t), "Parsed");
    } catch (err) {
      setNotice({ kind: "warn", text: errorText(err) });
    } finally {
      setParsing(false);
    }
  }

  // Auto-parse on paste — the operator shouldn't have to click "parse".
  // Read the textarea AFTER the browser applies the paste, then parse.
  function handlePaste() {
    setTimeout(() => {
      const value = textareaRef.current?.value ?? "";
      setText(value);
      if (value.trim().length > 15) void parse(value);
    }, 0);
  }

  // Self-learning: when the operator resolves a line to a library ingredient
  // whose name differs from the parsed text, remember the parsed name as an
  // alias so the same variant auto-matches next time.
  function chooseMatch(idx: number, ingredientId: string, sourceName?: string) {
    updateDecision(idx, { ingredientId, acceptNew: false });
    const chosen = ingredients.find((i) => String(i.id) === ingredientId);
    const learnFrom = (sourceName ?? "").trim();
    if (chosen && ingredientId && learnFrom &&
        learnFrom.toLowerCase() !== chosen.canonical_name.toLowerCase()) {
      // Operator-confirmed mapping → teach it. Ignore dup/hygiene rejections.
      api.addIngredientAlias(Number(ingredientId), learnFrom).catch(() => {});
    }
  }

  // Self-learning: discard a line that isn't an ingredient ("Add After
  // Cooking", section headers) and remember it so future imports drop it.
  function discardLine(idx: number, name: string) {
    setDiscarded((current) => new Set(current).add(idx));
    if (name.trim()) api.learnNonIngredient(name.trim()).catch(() => {});
    setNotice({ kind: "ok", text: `Discarded "${name}" — future imports will skip it.` });
  }

  async function importFromUrl() {
    if (!importUrl.trim()) return;
    setImporting(true);
    try {
      adoptDraft(await api.importRecipeUrl(importUrl.trim()), "Imported");
    } catch (err) {
      setNotice({ kind: "warn", text: errorText(err) });
    } finally {
      setImporting(false);
    }
  }

  async function importFromPhoto(file: File | null) {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setNotice({ kind: "warn", text: "Photo import accepts JPG, PNG, or WebP images." });
      return;
    }
    setImportingPhoto(true);
    try {
      const { mediaType, base64 } = await fileToBase64(file);
      adoptDraft(
        await api.importRecipePhoto({
          image_base64: base64,
          media_type: mediaType,
          filename: file.name,
        }),
        "Read photo",
      );
    } catch (err) {
      setNotice({ kind: "warn", text: errorText(err) });
    } finally {
      setImportingPhoto(false);
    }
  }

  const unresolved = parsed?.ingredients.filter((_, idx) => {
    if (discarded.has(idx)) return false;
    const d = decisions[idx];
    return !d?.ingredientId && !d?.acceptNew;
  }) ?? [];

  function updateDecision(idx: number, patch: Partial<ParsedDecision>) {
    setDecisions((current) => ({ ...current, [idx]: { ...current[idx], ...patch } }));
  }

  // Editing the name re-matches against the library (exact, plural-tolerant)
  // so a quick fix like "Onions" → "Onion" resolves without extra clicks.
  function editName(idx: number, value: string) {
    const norm = value.trim().toLowerCase();
    const singular = norm.replace(/s$/, "");
    const found = norm
      ? ingredients.find((i) => {
          const c = i.canonical_name.toLowerCase();
          return c === norm || c === singular || c.replace(/s$/, "") === singular;
        })
      : undefined;
    updateDecision(idx, found
      ? { nameText: value, ingredientId: String(found.id), acceptNew: false }
      : { nameText: value, ingredientId: "" });
  }

  async function save() {
    if (!parsed || unresolved.length) return;
    setSaving(true);
    try {
      const known = [...ingredients];
      const rows: RecipeInputIngredient[] = [];
      const createdIds: number[] = [];
      for (const [idx, line] of parsed.ingredients.entries()) {
        if (discarded.has(idx)) continue;
        const decision = decisions[idx];
        let ingredientId = Number(decision.ingredientId);
        const effectiveName = decision.oilChoice || decision.nameText.trim() || line.name_text;
        const existing = known.find((k) => k.canonical_name.toLowerCase() === effectiveName.toLowerCase());
        if (!ingredientId && decision.acceptNew && existing) {
          ingredientId = existing.id;
        } else if (!ingredientId && decision.acceptNew) {
          const created = await api.createIngredient({
            canonical_name: effectiveName,
            confidence: "needs_review",
            review_reason: `Created from smart paste: ${line.raw}`,
          });
          ingredientId = created.id;
          createdIds.push(created.id);
          known.push({
            id: created.id,
            canonical_name: effectiveName,
            confidence: "needs_review",
            nutrition_source: "manual",
            review_reason: `Created from smart paste: ${line.raw}`,
            density_g_per_cup: null,
            per_100g_calories: null,
            per_100g_protein_g: null,
            per_100g_carbs_g: null,
            per_100g_sugar_g: null,
            per_100g_fat_g: null,
            per_100g_fiber_g: null,
            per_100g_sodium_mg: null,
            last_fetch_status: null,
            updated_at: "",
          });
        }
        const useAlt = decision.useAlt && line.alt_quantity != null && line.alt_unit;
        // Inline corrections win over the parse; the original quantity_text
        // and range only survive when nothing was edited.
        const edited =
          decision.quantity !== (line.quantity?.toString() ?? "") ||
          decision.unit !== (line.unit ?? "");
        rows.push({
          position: idx + 1,
          ingredient_id: ingredientId,
          quantity: useAlt ? line.alt_quantity : numOrNull(decision.quantity),
          quantity_min: useAlt || edited ? null : line.quantity_min,
          quantity_max: useAlt || edited ? null : line.quantity_max,
          quantity_text: useAlt ? `${line.alt_quantity} ${line.alt_unit}` : edited ? null : line.quantity_text,
          unit: useAlt ? line.alt_unit : decision.unit.trim() || null,
          section: line.section,
          prep_note: line.prep_note,
          is_optional: line.is_optional,
          selected: line.alternative_to_index == null,
          // Positions are idx + 1, so the primary's index maps directly.
          alt_of: line.alternative_to_index != null ? line.alternative_to_index + 1 : null,
        });
      }
      setIngredients(known);
      const payload: CreateRecipeInput = {
        name: parsed.name ?? "Untitled recipe",
        description: parsed.description,
        source_url: parsed.source_url ?? null,
        servings: parsed.servings ?? 1,
        prep_min: parsed.prep_min,
        cook_min: parsed.cook_min,
        ingredients: rows,
        steps: parsed.steps.map((step, idx) => ({ ...step, position: idx + 1 })),
      };
      const created = await api.createRecipe(payload);
      // Newly-accepted ingredients have no nutrition yet — fetch it now
      // so the recipe detail opens with computed numbers instead of a
      // pile of needs-nutrition skips. Failures are non-fatal (the
      // nightly cron retries needs_review ingredients).
      if (createdIds.length) {
        setNotice({ kind: "ok", text: `Saved ${payload.name}. Fetching nutrition for ${createdIds.length} new ingredient(s)…` });
        await Promise.allSettled(createdIds.map((ingredientId) => api.fetchIngredient(ingredientId)));
      }
      setNotice({ kind: "ok", text: `Saved ${payload.name}.` });
      onCreated(created.id);
    } catch (err) {
      setNotice({ kind: "warn", text: errorText(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid gap-5 xl:grid-cols-[0.9fr_1.1fr]">
      <div>
        <label className="label">import from a link</label>
        <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto]">
          <input
            className="field"
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void importFromUrl(); }}
            placeholder="https://… (most recipe sites work)"
          />
          <button className="btn btn-primary" onClick={() => void importFromUrl()} disabled={importing || !importUrl.trim()}>
            {importing ? "importing…" : "import"}
          </button>
        </div>
        <label className="label mt-5 block">or upload a recipe photo</label>
        <div className="mt-2 rounded-2xl border border-dashed border-bg4 bg-bg p-4">
          <input
            className="field"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={importingPhoto}
            onChange={(e) => void importFromPhoto(e.currentTarget.files?.[0] ?? null)}
          />
          <p className="mt-2 text-xs text-muted">
            Best for cookbook pages, handwritten cards, or screenshots. Photo import returns the same review draft, so you still approve every line before save.
          </p>
          {importingPhoto && <p className="mt-2 text-sm text-accent">reading photo…</p>}
        </div>
        <label className="label mt-5 block">or paste raw recipe text</label>
        <textarea
          ref={textareaRef}
          className="field mt-2 min-h-[28rem] font-mono"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onPaste={handlePaste}
          placeholder="Paste recipe here — it parses automatically…"
        />
        <div className="mt-3 flex gap-2">
          <button className="btn btn-primary" onClick={() => void parse()} disabled={!text.trim() || parsing}>
            {parsing ? "parsing…" : "parse"}
          </button>
          <button className="btn" onClick={() => { setText(""); setImportUrl(""); setParsed(null); }}>clear</button>
        </div>
      </div>
      <div>
        {!parsed ? (
          <div className="rounded-2xl border border-dashed border-bg4 p-8 text-muted">Parsed review appears here. Nothing is saved until you approve and click save.</div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-2xl border border-bg4 bg-bg p-4">
              <div className="label">draft</div>
              <div className="mt-2 text-2xl font-bold">{parsed.name ?? "Untitled recipe"}</div>
              <div className="mt-1 text-sm text-muted">serves {parsed.servings ?? "?"} · {parsed.steps.length} steps</div>
              {parsed.warnings.length > 0 && <div className="mt-3 text-sm text-warn">{parsed.warnings.join(" · ")}</div>}
            </div>
            <div className="space-y-2">
              {parsed.ingredients.map((line, idx) => {
                if (discarded.has(idx)) return null;
                const decision = decisions[idx] ?? defaultDecision(line);
                const matchedName = decision.ingredientId
                  ? ingredients.find((i) => String(i.id) === decision.ingredientId)?.canonical_name ?? null
                  : null;
                // Resolved rows (matched or accepted) read green/accent so
                // only the ones still needing a decision stand out.
                const tone = matchedName
                  ? "border-herb/35"
                  : decision.acceptNew
                    ? "border-accent/50"
                    : line.parse_confidence === "parsed" ? "border-bg4" : line.parse_confidence === "partial" ? "border-warn/50" : "border-danger/50";
                return (
                  <div key={`${line.raw}-${idx}`} className={`rounded-2xl border ${tone} bg-bg p-3`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{decision.nameText || line.name_text}</span>
                      <span className="pill">{line.parse_confidence}</span>
                      {line.section && <span className="pill border-accent/40 text-accent">{line.section}</span>}
                      {matchedName && <span className="pill border-herb/40 text-accent2">✓ matched · {matchedName} — no action needed</span>}
                      {!matchedName && decision.acceptNew && <span className="pill border-accent/40 text-accent">will create as new</span>}
                      {!matchedName && !decision.acceptNew && <span className="pill border-warn/40 text-warn">needs a decision</span>}
                      {line.is_optional && <span className="pill border-warn/40 text-warn">optional</span>}
                      {line.alternative_to_index != null && (
                        <span className="pill border-accent/40 text-accent">
                          or — alternative to {parsed.ingredients[line.alternative_to_index]?.name_text ?? "previous"}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-muted">{line.raw}</div>
                    <div className="mt-2 grid gap-2 md:grid-cols-[1fr_5rem_6rem]">
                      <input
                        className="field"
                        value={decision.nameText}
                        onChange={(e) => editName(idx, e.target.value)}
                        placeholder="ingredient name"
                        title="Fix the parsed name — re-matches the library as you type"
                      />
                      <input className="field" value={decision.quantity} onChange={(e) => updateDecision(idx, { quantity: e.target.value })} placeholder="qty" />
                      <input className="field" value={decision.unit} onChange={(e) => updateDecision(idx, { unit: e.target.value })} placeholder="unit" />
                    </div>
                    <div className="mt-2 grid gap-2 md:grid-cols-[1fr_auto]">
                      <select className="field" value={decision.ingredientId} onChange={(e) => chooseMatch(idx, e.target.value, decision.nameText || line.name_text)}>
                        <option value="">No library match</option>
                        {ingredients.map((ingredient) => (
                          <option key={ingredient.id} value={ingredient.id}>{ingredient.canonical_name}</option>
                        ))}
                      </select>
                      <button className={`btn ${decision.acceptNew ? "border-accent text-accent" : ""}`} onClick={() => updateDecision(idx, { acceptNew: !decision.acceptNew, ingredientId: "" })}>
                        accept as new
                      </button>
                      <button
                        className="btn text-muted hover:border-warn hover:text-warn"
                        title="Not an ingredient — discard and remember it (future imports skip it)"
                        onClick={() => discardLine(idx, decision.nameText || line.name_text)}
                      >
                        discard
                      </button>
                    </div>
                    {!matchedName && (() => {
                      const similar = similarIngredients(decision.nameText || line.name_text, ingredients);
                      return similar.length > 0 ? (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted">
                          <span>{decision.acceptNew ? "creating new — but similar exists:" : "similar in library:"}</span>
                          {similar.map((s) => (
                            <button
                              key={s.id}
                              className="pill hover:border-accent hover:text-accent"
                              onClick={() => chooseMatch(idx, String(s.id), decision.nameText || line.name_text)}
                            >
                              {s.canonical_name}
                            </button>
                          ))}
                        </div>
                      ) : null;
                    })()}
                    {GENERIC_OIL_NAMES.has(line.name_text.toLowerCase()) && (
                      <select
                        className="field mt-3"
                        value={decision.oilChoice}
                        onChange={(e) => {
                          const choice = e.target.value;
                          const existing = choice
                            ? ingredients.find((i) => i.canonical_name.toLowerCase() === choice.toLowerCase())
                            : undefined;
                          updateDecision(idx, existing
                            ? { oilChoice: choice, ingredientId: String(existing.id), acceptNew: false }
                            : { oilChoice: choice, ingredientId: "", acceptNew: Boolean(choice) });
                        }}
                      >
                        <option value="">generic “oil” — choose the kind…</option>
                        {OIL_TYPES.map((oil) => <option key={oil} value={oil}>{oil}</option>)}
                      </select>
                    )}
                    {line.alt_quantity != null && line.alt_unit && (
                      <label className="mt-3 flex items-center gap-2 text-sm text-muted">
                        <input type="checkbox" checked={decision.useAlt} onChange={(e) => updateDecision(idx, { useAlt: e.target.checked })} />
                        use alternate {fmt(line.alt_quantity, 2)} {line.alt_unit} instead of {line.quantity_text ?? `${line.quantity ?? ""} ${line.unit ?? ""}`}
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
            <button className="btn btn-primary" onClick={() => void save()} disabled={saving || unresolved.length > 0}>
              {unresolved.length ? `resolve ${unresolved.length} ingredient(s)` : saving ? "saving…" : "save recipe"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface StructuredIngredientRow {
  ingredientId: string;
  quantity: string;
  unit: string;
  prepNote: string;
  optional: boolean;
}

function StructuredRecipeForm({
  ingredients,
  onCreated,
  setNotice,
}: {
  ingredients: Ingredient[];
  onCreated: (id: number) => void;
  setNotice: (notice: Notice) => void;
}) {
  const [name, setName] = useState("");
  const [servings, setServings] = useState("4");
  const [description, setDescription] = useState("");
  const [rows, setRows] = useState<StructuredIngredientRow[]>([
    { ingredientId: "", quantity: "", unit: "g", prepNote: "", optional: false },
  ]);
  const [stepText, setStepText] = useState("");

  function updateRow(idx: number, patch: Partial<StructuredIngredientRow>) {
    setRows((current) => current.map((row, i) => i === idx ? { ...row, ...patch } : row));
  }

  async function save() {
    const recipeIngredients = rows
      .filter((row) => row.ingredientId)
      .map((row, idx) => ({
        position: idx + 1,
        ingredient_id: Number(row.ingredientId),
        quantity: numOrNull(row.quantity),
        unit: row.unit || null,
        prep_note: row.prepNote || null,
        is_optional: row.optional,
      }));
    const steps: RecipeStep[] = stepText
      .split(/\n+/)
      .map((text) => text.trim())
      .filter(Boolean)
      .map((text, idx) => ({ position: idx + 1, title: null, text, timer_minutes: null }));
    try {
      const created = await api.createRecipe({
        name,
        description: description || null,
        servings: Number(servings),
        ingredients: recipeIngredients,
        steps,
      });
      setNotice({ kind: "ok", text: `Saved ${name}.` });
      onCreated(created.id);
    } catch (err) {
      setNotice({ kind: "warn", text: errorText(err) });
    }
  }

  return (
    <div className="grid gap-4 xl:grid-cols-[0.8fr_1.2fr]">
      <div className="space-y-3">
        <label className="label">name</label>
        <input className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder="Recipe name" />
        <label className="label">servings</label>
        <input className="field" value={servings} onChange={(e) => setServings(e.target.value)} />
        <label className="label">description</label>
        <textarea className="field min-h-28" value={description} onChange={(e) => setDescription(e.target.value)} />
        <label className="label">steps, one per line</label>
        <textarea className="field min-h-44" value={stepText} onChange={(e) => setStepText(e.target.value)} />
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="label">ingredients</div>
          <button className="btn" onClick={() => setRows((current) => [...current, { ingredientId: "", quantity: "", unit: "g", prepNote: "", optional: false }])}>add row</button>
        </div>
        {rows.map((row, idx) => (
          <div key={idx} className="grid gap-2 rounded-2xl border border-bg4 bg-bg p-3 md:grid-cols-[1fr_7rem_6rem]">
            <select className="field" value={row.ingredientId} onChange={(e) => updateRow(idx, { ingredientId: e.target.value })}>
              <option value="">Choose ingredient</option>
              {ingredients.map((ingredient) => <option key={ingredient.id} value={ingredient.id}>{ingredient.canonical_name}</option>)}
            </select>
            <input className="field" value={row.quantity} onChange={(e) => updateRow(idx, { quantity: e.target.value })} placeholder="qty" />
            <input className="field" value={row.unit} onChange={(e) => updateRow(idx, { unit: e.target.value })} placeholder="unit" />
            <input className="field md:col-span-2" value={row.prepNote} onChange={(e) => updateRow(idx, { prepNote: e.target.value })} placeholder="prep note" />
            <label className="flex items-center gap-2 text-sm text-muted">
              <input type="checkbox" checked={row.optional} onChange={(e) => updateRow(idx, { optional: e.target.checked })} />
              optional
            </label>
          </div>
        ))}
        <button className="btn btn-primary" onClick={() => void save()} disabled={!name.trim() || !Number(servings)}>save structured recipe</button>
      </div>
    </div>
  );
}

function RecipeScreen({
  selectedId,
  setSelectedId,
  onCook,
  onOpenIngredient,
}: {
  selectedId: number | null;
  setSelectedId: (id: number | null) => void;
  onCook: (id: number) => void;
  onOpenIngredient: (ingredientId: number) => void;
}) {
  const [refreshToken, setRefreshToken] = useState(0);

  function refresh() {
    setRefreshToken((n) => n + 1);
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[22rem_1fr]">
      <RecipeList
        selectedId={selectedId}
        onSelect={setSelectedId}
        refreshToken={refreshToken}
        onBulkDeleted={(deletedIds) => {
          if (selectedId != null && deletedIds.includes(selectedId)) setSelectedId(null);
          refresh();
        }}
      />
      <RecipeDetailPanel
        id={selectedId}
        onChanged={refresh}
        onDeleted={() => {
          setSelectedId(null);
          refresh();
        }}
        onCook={onCook}
        onOpenIngredient={onOpenIngredient}
      />
    </div>
  );
}

function IngredientLibraryScreen({ focusIngredientId }: { focusIngredientId: number | null }) {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<Ingredient[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(focusIngredientId);
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());
  // Ingredients auto-fetched this mount — once per visit, no retry loops.
  const autoFetched = useRef<Set<number>>(new Set());
  const [detail, setDetail] = useState<Ingredient | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [canonicalName, setCanonicalName] = useState("");
  const [alias, setAlias] = useState("");
  const [unitName, setUnitName] = useState("");
  const [gramsPerUnit, setGramsPerUnit] = useState("");
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [nutrients, setNutrients] = useState({
    per_100g_calories: "",
    per_100g_protein_g: "",
    per_100g_carbs_g: "",
    per_100g_sugar_g: "",
    per_100g_fat_g: "",
    per_100g_fiber_g: "",
    per_100g_sodium_mg: "",
    density_g_per_cup: "",
  });

  function loadList() {
    api.listIngredients({ search, limit: 1000, sort: "review" })
      .then((data) => {
        setItems(data);
        if (!selectedId && data[0]) setSelectedId(data[0].id);
      })
      .catch((err: unknown) => setNotice({ kind: "warn", text: errorText(err) }));
  }

  useEffect(loadList, [search, selectedId]);

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    (async () => {
      try {
        let ingredient = await api.getIngredient(selectedId);
        // Missing nutrition? Fetch it automatically — visiting this page
        // is the operator's signal to fill the gap, no extra click needed.
        const needsFetch = ingredient.per_100g_calories == null || ingredient.confidence === "needs_review";
        if (needsFetch && !autoFetched.current.has(selectedId)) {
          autoFetched.current.add(selectedId);
          try {
            const result = await api.fetchIngredient(selectedId);
            ingredient = await api.getIngredient(selectedId);
            if (!cancelled && ingredient.per_100g_calories != null) {
              setNotice({ kind: "ok", text: `Auto-fetched: ${fetchOutcomeText(result.outcome)}` });
            }
          } catch {
            // Provider miss or rate limit — leave the manual button as recourse.
          }
        }
        if (cancelled) return;
        setDetail(ingredient);
        setCanonicalName(ingredient.canonical_name);
        setNutrients(ingredientNutritionForm(ingredient));
      } catch (err) {
        if (!cancelled) setNotice({ kind: "warn", text: errorText(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  async function saveNutrition() {
    if (!detail) return;
    try {
      await api.patchIngredient(detail.id, {
        per_100g_calories: numOrNull(nutrients.per_100g_calories),
        per_100g_protein_g: numOrNull(nutrients.per_100g_protein_g),
        per_100g_carbs_g: numOrNull(nutrients.per_100g_carbs_g),
        per_100g_sugar_g: numOrNull(nutrients.per_100g_sugar_g),
        per_100g_fat_g: numOrNull(nutrients.per_100g_fat_g),
        per_100g_fiber_g: numOrNull(nutrients.per_100g_fiber_g),
        per_100g_sodium_mg: numOrNull(nutrients.per_100g_sodium_mg),
        density_g_per_cup: numOrNull(nutrients.density_g_per_cup),
        confidence: "estimated",
        nutrition_source: "manual_override",
      });
      setNotice({ kind: "ok", text: "Ingredient nutrition saved." });
      setSelectedId(detail.id);
      loadList();
    } catch (err) {
      setNotice({ kind: "warn", text: errorText(err) });
    }
  }

  async function saveIngredientName() {
    if (!detail || !canonicalName.trim()) return;
    try {
      await api.patchIngredient(detail.id, { canonical_name: canonicalName.trim() });
      const refreshed = await api.getIngredient(detail.id);
      setDetail(refreshed);
      setCanonicalName(refreshed.canonical_name);
      setNotice({ kind: "ok", text: "Ingredient name updated." });
      loadList();
    } catch (err) {
      setNotice({ kind: "warn", text: errorText(err) });
    }
  }

  async function deleteIngredient() {
    if (!detail) return;
    const ok = window.confirm(`Delete ingredient "${detail.canonical_name}"? This only works if no recipe uses it.`);
    if (!ok) return;
    try {
      await api.deleteIngredient(detail.id);
      setNotice({ kind: "ok", text: `Deleted ${detail.canonical_name}.` });
      setDetail(null);
      setSelectedId(null);
      loadList();
    } catch (err) {
      setNotice({ kind: "warn", text: errorText(err) });
    }
  }

  function toggleChecked(id: number, checked: boolean) {
    setCheckedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function deleteChecked() {
    const ids = [...checkedIds];
    if (!ids.length) return;
    const ok = window.confirm(`Delete ${ids.length} ingredient(s)? Ingredients used by a recipe are skipped.`);
    if (!ok) return;
    const results = await Promise.allSettled(ids.map((id) => api.deleteIngredient(id)));
    const deleted = ids.filter((_, i) => results[i].status === "fulfilled");
    const failedNames = ids
      .map((id, i) => ({ id, result: results[i] }))
      .filter((x) => x.result.status === "rejected")
      .map((x) => items.find((it) => it.id === x.id)?.canonical_name ?? `#${x.id}`);
    setCheckedIds(new Set());
    setNotice(failedNames.length
      ? { kind: "warn", text: `Deleted ${deleted.length}. Could not delete (still used by recipes): ${failedNames.join(", ")}.` }
      : { kind: "ok", text: `Deleted ${deleted.length} ingredient(s).` });
    if (selectedId != null && deleted.includes(selectedId)) {
      setDetail(null);
      setSelectedId(null);
    }
    loadList();
  }

  async function addAlias() {
    if (!detail || !alias.trim()) return;
    await api.addIngredientAlias(detail.id, alias.trim());
    setAlias("");
    setDetail(await api.getIngredient(detail.id));
  }

  async function addUnit() {
    if (!detail || !unitName.trim() || !Number(gramsPerUnit)) return;
    await api.addHouseholdUnit(detail.id, { unit_name: unitName.trim(), grams_per_unit: Number(gramsPerUnit), notes: null });
    setUnitName("");
    setGramsPerUnit("");
    setDetail(await api.getIngredient(detail.id));
  }

  async function togglePantryStaple(checked: boolean) {
    if (!detail) return;
    try {
      await api.patchIngredient(detail.id, { pantry_staple: checked });
      setDetail({ ...detail, pantry_staple: checked ? 1 : 0 });
      setNotice({ kind: "ok", text: checked
        ? `${detail.canonical_name} marked as pantry staple — shopping lists tuck it into "from your pantry".`
        : `${detail.canonical_name} is back on the buy list.` });
    } catch (err) {
      setNotice({ kind: "warn", text: errorText(err) });
    }
  }

  async function mergeInto() {
    if (!detail || !mergeTargetId) return;
    const target = items.find((i) => i.id === Number(mergeTargetId));
    const ok = window.confirm(
      `Merge "${detail.canonical_name}" into "${target?.canonical_name ?? "selected ingredient"}"? ` +
      "All recipes and shopping lists will point at the target; this cannot be undone.",
    );
    if (!ok) return;
    try {
      const res = await api.mergeIngredient(detail.id, Number(mergeTargetId));
      setNotice({ kind: "ok", text: `Merged into ${res.target_name}; old name kept as an alias.` });
      setMergeTargetId("");
      setDetail(null);
      setSelectedId(res.target_id);
      loadList();
    } catch (err) {
      setNotice({ kind: "warn", text: errorText(err) });
    }
  }

  async function fetchNow() {
    if (!detail) return;
    try {
      const result = await api.fetchIngredient(detail.id);
      const refreshed = await api.getIngredient(detail.id);
      setNotice({
        kind: result.outcome.status === "hit" ? "ok" : "warn",
        text: fetchOutcomeText(result.outcome),
      });
      setDetail(refreshed);
      setCanonicalName(refreshed.canonical_name);
      setNutrients(ingredientNutritionForm(refreshed));
      loadList();
    } catch (err) {
      setNotice({ kind: "warn", text: errorText(err) });
    }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[24rem_1fr]">
      <section className="panel overflow-hidden">
        <div className="border-b border-bg4 p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="label">ingredient library</div>
            {checkedIds.size > 0 && (
              <button
                className="btn border-danger/50 text-danger hover:border-danger hover:text-danger"
                onClick={() => void deleteChecked()}
              >
                delete {checkedIds.size} selected
              </button>
            )}
          </div>
          <input className="field mt-3" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ingredients…" />
        </div>
        <div className="max-h-[72vh] overflow-auto p-2">
          {items.map((item) => (
            <div key={item.id} onClick={() => setSelectedId(item.id)} className={`mb-2 w-full cursor-pointer rounded-xl border p-3 text-left ${selectedId === item.id ? "border-accent bg-accent/10" : "border-bg4 bg-bg"}`}>
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={checkedIds.has(item.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => toggleChecked(item.id, e.target.checked)}
                />
                <div className="font-semibold">{item.canonical_name}</div>
              </div>
              <div className="mt-1 flex flex-wrap gap-2 pl-6">
                <span className={`pill ${item.confidence === "needs_review" ? "border-warn/40 text-warn" : "border-herb/40 text-accent2"}`}>{item.confidence}</span>
                {item.last_fetch_status && <span className="pill">{item.last_fetch_status}</span>}
                {Boolean(item.review_severity) && (
                  <span className={`pill ${item.review_severity! >= 3 ? "border-danger/40 text-danger" : "border-warn/40 text-warn"}`}>
                    {item.review_reasons?.[0] ?? "review"}
                    {(item.review_reasons?.length ?? 0) > 1 ? ` +${(item.review_reasons?.length ?? 1) - 1}` : ""}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
      <section className="panel p-5">
        <NoticeBar notice={notice} />
        {!detail ? (
          <p className="text-muted">Choose an ingredient.</p>
        ) : (
          <div className="mt-4 space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="label">selected ingredient</div>
                <h2 className="mt-1 text-3xl font-extrabold">{detail.canonical_name}</h2>
                {detail.review_reason && <p className="mt-2 text-sm text-warn">{detail.review_reason}</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                <button className="btn btn-primary" onClick={() => void fetchNow()}>fetch now</button>
                <button className="btn border-danger/50 text-danger hover:border-danger hover:text-danger" onClick={() => void deleteIngredient()}>delete</button>
              </div>
            </div>
            <div className="rounded-2xl border border-bg4 bg-bg p-3">
              <label className="label">ingredient name</label>
              <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto]">
                <input className="field" value={canonicalName} onChange={(e) => setCanonicalName(e.target.value)} />
                <button className="btn" onClick={() => void saveIngredientName()} disabled={!canonicalName.trim() || canonicalName.trim() === detail.canonical_name}>
                  save name
                </button>
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm text-muted">
                <input
                  type="checkbox"
                  checked={detail.pantry_staple === 1}
                  onChange={(e) => void togglePantryStaple(e.target.checked)}
                />
                pantry staple — always at home; shopping lists show it under "from your pantry" instead of the buy list
              </label>
            </div>
            {detail.curated_available && (
              <div className="rounded-xl border border-accent/40 bg-accent/10 p-3 text-sm text-accent">
                A curated profile exists for "{detail.canonical_name}" — hit{" "}
                <span className="font-semibold">fetch now</span> to apply its literature values and density.
              </div>
            )}
            <div className="grid gap-3 md:grid-cols-4">
              {INGREDIENT_NUTRIENT_FIELDS.map(({ key, label }) => (
                <label key={key} className="space-y-1">
                  <span className="label">{label}</span>
                  <input className="field" value={nutrients[key]} onChange={(e) => setNutrients((n) => ({ ...n, [key]: e.target.value }))} />
                </label>
              ))}
            </div>
            <button className="btn btn-primary" onClick={() => void saveNutrition()}>save nutrition</button>
            <div className="grid gap-4 xl:grid-cols-2">
              <Block title="Aliases">
                <div className="mb-3 flex flex-wrap gap-2">
                  {(detail.aliases ?? []).map((a) => <span key={a} className="pill">{a}</span>)}
                </div>
                <div className="flex gap-2">
                  <input className="field" value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="new alias" />
                  <button className="btn" onClick={() => void addAlias()}>add</button>
                </div>
              </Block>
              <Block title="Household units">
                <div className="mb-3 space-y-2">
                  {(detail.household_units ?? []).length === 0 && (
                    <p className="text-sm text-muted">
                      None yet. A household unit maps a count to grams (e.g. "1 medium onion = 110 g")
                      so recipes can say "2 medium onions" and still compute nutrition.
                    </p>
                  )}
                  {(detail.household_units ?? []).map((u) => (
                    <div key={u.unit_name} className="rounded-xl border border-bg4 bg-bg p-3 text-sm">
                      {u.unit_name}: {fmt(u.grams_per_unit, 1)} g
                    </div>
                  ))}
                </div>
                <div className="grid gap-2 sm:grid-cols-[1fr_8rem_auto]">
                  <input className="field" value={unitName} onChange={(e) => setUnitName(e.target.value)} placeholder="e.g. medium onion" />
                  <input className="field" value={gramsPerUnit} onChange={(e) => setGramsPerUnit(e.target.value)} placeholder="g per unit" />
                  <button className="btn" onClick={() => void addUnit()}>add</button>
                </div>
              </Block>
              <Block title="Merge into another ingredient">
                <p className="mb-3 text-sm text-muted">
                  Duplicate entry ("Chicken" vs "Chicken Breast")? Merging rewrites every recipe and
                  shopping list to the target and keeps this name as an alias.
                </p>
                <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                  <select className="field" value={mergeTargetId} onChange={(e) => setMergeTargetId(e.target.value)}>
                    <option value="">Choose target ingredient…</option>
                    {items.filter((i) => i.id !== detail.id).map((i) => (
                      <option key={i.id} value={i.id}>{i.canonical_name}</option>
                    ))}
                  </select>
                  <button className="btn border-warn/50 text-warn hover:border-warn" disabled={!mergeTargetId} onClick={() => void mergeInto()}>
                    merge
                  </button>
                </div>
              </Block>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

const PLAN_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const PLAN_SLOTS = ["breakfast", "lunch", "dinner", "snack"];

function MealPlanScreen({ onListCreated }: { onListCreated: () => void }) {
  const [recipes, setRecipes] = useState<RecipeListItem[]>([]);
  const [entries, setEntries] = useState<MealPlanEntry[]>([]);
  const [notice, setNotice] = useState<Notice>(null);
  const [draft, setDraft] = useState({ day: "0", slot: "dinner", recipeId: "", multiplier: "1" });
  const [listName, setListName] = useState("");
  const [building, setBuilding] = useState(false);

  function loadPlan() {
    api.getMealPlan()
      .then((data) => setEntries(data.entries))
      .catch((err: unknown) => setNotice({ kind: "warn", text: errorText(err) }));
  }

  useEffect(() => {
    api.listRecipes().then(setRecipes).catch((err: unknown) => setNotice({ kind: "warn", text: errorText(err) }));
    loadPlan();
  }, []);

  async function addEntry() {
    if (!draft.recipeId) return;
    try {
      await api.addMealPlanEntry({
        day: Number(draft.day),
        meal_slot: draft.slot,
        recipe_id: Number(draft.recipeId),
        servings_multiplier: Number(draft.multiplier) || 1,
      });
      setNotice(null);
      loadPlan();
    } catch (err) {
      setNotice({ kind: "warn", text: errorText(err) });
    }
  }

  async function removeEntry(id: number) {
    try {
      await api.deleteMealPlanEntry(id);
      loadPlan();
    } catch (err) {
      setNotice({ kind: "warn", text: errorText(err) });
    }
  }

  async function clearPlan() {
    try {
      await api.clearMealPlan();
      loadPlan();
    } catch (err) {
      setNotice({ kind: "warn", text: errorText(err) });
    }
  }

  async function buildShoppingList() {
    setBuilding(true);
    try {
      await api.mealPlanToShoppingList(listName.trim() || null);
      setNotice({ kind: "ok", text: "Shopping list created from the plan." });
      onListCreated();
    } catch (err) {
      setNotice({ kind: "warn", text: errorText(err) });
    } finally {
      setBuilding(false);
    }
  }

  return (
    <div className="grid gap-5 xl:grid-cols-[26rem_1fr]">
      <section className="panel p-5">
        <NoticeBar notice={notice} />
        <div className="mt-4 space-y-4">
          <div>
            <div className="label">add to the week</div>
            <div className="mt-2 grid gap-2">
              <select className="field" value={draft.recipeId} onChange={(e) => setDraft((d) => ({ ...d, recipeId: e.target.value }))}>
                <option value="">Choose recipe</option>
                {recipes.map((recipe) => <option key={recipe.id} value={recipe.id}>{recipe.name}</option>)}
              </select>
              <div className="grid grid-cols-[1fr_1fr_5rem] gap-2">
                <select className="field" value={draft.day} onChange={(e) => setDraft((d) => ({ ...d, day: e.target.value }))}>
                  {PLAN_DAYS.map((day, idx) => <option key={day} value={idx}>{day}</option>)}
                </select>
                <select className="field" value={draft.slot} onChange={(e) => setDraft((d) => ({ ...d, slot: e.target.value }))}>
                  {PLAN_SLOTS.map((slot) => <option key={slot} value={slot}>{slot}</option>)}
                </select>
                <input
                  className="field"
                  type="number"
                  min="0.25"
                  step="0.25"
                  value={draft.multiplier}
                  onChange={(e) => setDraft((d) => ({ ...d, multiplier: e.target.value }))}
                  title="Servings multiplier (1 = as written)"
                />
              </div>
              <button className="btn btn-primary" disabled={!draft.recipeId} onClick={() => void addEntry()}>
                add to plan
              </button>
            </div>
          </div>
          <div className="border-t border-bg4 pt-4">
            <div className="label">one list for the whole week</div>
            <input
              className="field mt-2"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder={`Weekly plan — ${new Date().toISOString().slice(0, 10)}`}
            />
            <div className="mt-2 flex gap-2">
              <button
                className="btn btn-primary flex-1"
                disabled={building || entries.length === 0}
                onClick={() => void buildShoppingList()}
              >
                {building ? "building…" : "make shopping list"}
              </button>
              <button className="btn" disabled={entries.length === 0} onClick={() => void clearPlan()}>
                clear week
              </button>
            </div>
            {entries.length === 0 && (
              <p className="mt-3 text-xs text-muted">Plan at least one meal to build a list.</p>
            )}
          </div>
        </div>
      </section>
      <section className="panel p-5">
        <div className="label">this week</div>
        <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-1">
          {PLAN_DAYS.map((day, idx) => {
            const dayEntries = entries.filter((entry) => entry.day === idx);
            return (
              <div key={day} className="rounded-xl border border-bg4 bg-bg p-3">
                <div className="text-xs font-bold uppercase tracking-wider text-accent">{day}</div>
                {dayEntries.length === 0 ? (
                  <div className="mt-1 text-sm text-muted">—</div>
                ) : (
                  <div className="mt-2 space-y-2">
                    {dayEntries.map((entry) => (
                      <div key={entry.id} className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="pill border-bg4 text-muted">{entry.meal_slot}</span>
                        <span className="font-semibold text-fg">{entry.recipe_name}</span>
                        {entry.servings_multiplier !== 1 && (
                          <span className="text-xs text-muted">×{fmt(entry.servings_multiplier, 2)}</span>
                        )}
                        <button
                          className="ml-auto text-xs text-muted hover:text-warn"
                          title="Remove from plan"
                          onClick={() => void removeEntry(entry.id)}
                        >
                          remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function ShoppingListScreen() {
  const [recipes, setRecipes] = useState<RecipeListItem[]>([]);
  const [lists, setLists] = useState<ShoppingListSummary[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ShoppingListDetail | null>(null);
  const [nutrition, setNutrition] = useState<ShoppingListNutrition | null>(null);
  const [name, setName] = useState("");
  const [rows, setRows] = useState([{ recipeId: "", multiplier: "1" }]);
  const [notice, setNotice] = useState<Notice>(null);

  function loadLists() {
    api.listShoppingLists().then((data) => {
      setLists(data);
      if (!selectedId && data[0]) setSelectedId(data[0].id);
    }).catch((err: unknown) => setNotice({ kind: "warn", text: errorText(err) }));
  }

  useEffect(() => {
    api.listRecipes().then(setRecipes).catch((err: unknown) => setNotice({ kind: "warn", text: errorText(err) }));
    loadLists();
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    Promise.all([api.getShoppingList(selectedId), api.getShoppingNutrition(selectedId)])
      .then(([list, nut]) => {
        setDetail(list);
        setNutrition(nut);
      })
      .catch((err: unknown) => setNotice({ kind: "warn", text: errorText(err) }));
  }, [selectedId]);

  async function createList() {
    const selected = rows
      .filter((row) => row.recipeId)
      .map((row) => ({ recipe_id: Number(row.recipeId), servings_multiplier: Number(row.multiplier) || 1 }));
    if (!selected.length) return;
    try {
      const created = await api.createShoppingList({ name: name || null, recipes: selected });
      setNotice({ kind: "ok", text: "Shopping list created." });
      setSelectedId(created.id);
      loadLists();
    } catch (err) {
      setNotice({ kind: "warn", text: errorText(err) });
    }
  }

  async function toggleItem(itemId: number, checked: boolean) {
    if (!detail) return;
    await api.checkShoppingItem(detail.id, itemId, checked);
    setDetail(await api.getShoppingList(detail.id));
  }

  async function copyListText() {
    if (!detail) return;
    const sectionOrder = ["produce", "meat & fish", "dairy & eggs", "pantry", "spices"];
    const buy = detail.items.filter((i) => !i.is_pantry_staple && !i.checked);
    const sections = [...sectionOrder, ...[...new Set(buy.map((i) => i.section))].filter((s) => !sectionOrder.includes(s))];
    const lines: string[] = [`Shopping list — ${detail.name ?? `#${detail.id}`}`];
    for (const section of sections) {
      const items = buy.filter((i) => i.section === section);
      if (!items.length) continue;
      lines.push("", section.toUpperCase());
      for (const item of items) {
        const qty = item.is_spice
          ? "as needed"
          : `${fmt(item.quantity, 2)} ${item.unit}${item.friendly_quantity ? ` (${item.friendly_quantity})` : ""}`;
        lines.push(`- ${item.ingredient_name} — ${qty}${item.is_optional ? " (optional)" : ""}`);
      }
    }
    const staples = detail.items.filter((i) => i.is_pantry_staple);
    if (staples.length) {
      lines.push("", `From your pantry: ${staples.map((i) => i.ingredient_name).join(", ")}`);
    }
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setNotice({ kind: "ok", text: "List copied — paste it anywhere." });
    } catch {
      setNotice({ kind: "warn", text: "Clipboard blocked by the browser — select and copy manually." });
    }
  }

  return (
    <div className="grid gap-5 xl:grid-cols-[30rem_1fr]">
      <section className="panel p-5">
        <NoticeBar notice={notice} />
        <div className="mt-4 space-y-4">
          <div>
            <div className="label">new shopping list</div>
            <input className="field mt-2" value={name} onChange={(e) => setName(e.target.value)} placeholder="Weekly shop" />
          </div>
          {rows.map((row, idx) => (
            <div key={idx} className="grid gap-2 sm:grid-cols-[1fr_7rem]">
              <select className="field" value={row.recipeId} onChange={(e) => setRows((current) => current.map((r, i) => i === idx ? { ...r, recipeId: e.target.value } : r))}>
                <option value="">Choose recipe</option>
                {recipes.map((recipe) => <option key={recipe.id} value={recipe.id}>{recipe.name}</option>)}
              </select>
              <input className="field" value={row.multiplier} onChange={(e) => setRows((current) => current.map((r, i) => i === idx ? { ...r, multiplier: e.target.value } : r))} />
            </div>
          ))}
          <div className="flex gap-2">
            <button className="btn" onClick={() => setRows((current) => [...current, { recipeId: "", multiplier: "1" }])}>add recipe</button>
            <button className="btn btn-primary" onClick={() => void createList()}>create list</button>
          </div>
          <Block title="Existing lists">
            <div className="space-y-2">
              {lists.map((list) => (
                <button key={list.id} className={`w-full rounded-xl border p-3 text-left ${selectedId === list.id ? "border-accent bg-accent/10" : "border-bg4 bg-bg"}`} onClick={() => setSelectedId(list.id)}>
                  {list.name ?? `List #${list.id}`} · {list.status}
                </button>
              ))}
            </div>
          </Block>
        </div>
      </section>
      <section className="panel p-5">
        {!detail ? (
          <p className="text-muted">Create or choose a shopping list.</p>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="label">shopping list</div>
                <h2 className="mt-1 text-3xl font-extrabold">{detail.name ?? `List #${detail.id}`}</h2>
              </div>
              <div className="flex items-center gap-2">
                <button className="btn" onClick={() => void copyListText()}>copy list</button>
                {nutrition && <span className={`pill ${nutrition.is_complete ? "border-herb/40 text-accent2" : "border-warn/40 text-warn"}`}>{nutrition.is_complete ? "nutrition complete" : "nutrition incomplete"}</span>}
              </div>
            </div>
            {nutrition && (
              <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                <Nutrient label="calories" value={nutrition.combined_total.calories} />
                <Nutrient label="protein" value={nutrition.combined_total.protein_g} unit="g" />
                <Nutrient label="carbs" value={nutrition.combined_total.carbs_g} unit="g" />
                <Nutrient label="fat" value={nutrition.combined_total.fat_g} unit="g" />
              </div>
            )}
            <div className="space-y-4">
              {(() => {
                const renderItem = (item: ShoppingListItem) => (
                  <label key={item.id} className={`flex items-center gap-3 rounded-xl border p-3 ${item.checked ? "border-herb/30 bg-herb/10 text-muted line-through" : item.is_optional ? "border-warn/40 bg-warn/10" : "border-bg4 bg-bg"}`}>
                    <input type="checkbox" checked={item.checked} onChange={(e) => void toggleItem(item.id, e.target.checked)} />
                    <span className="flex-1 font-semibold">{item.ingredient_name}</span>
                    {item.is_spice
                      ? <span className="text-sm italic text-muted">as needed</span>
                      : (
                        <span className="font-mono text-sm">
                          {fmt(item.quantity, 2)} {item.unit}
                          {item.friendly_quantity ? <span className="text-muted"> ({item.friendly_quantity})</span> : null}
                        </span>
                      )}
                    {item.is_optional && <span className="pill border-warn/40 text-warn">optional</span>}
                  </label>
                );
                const buy = detail.items.filter((i) => !i.is_pantry_staple);
                const staples = detail.items.filter((i) => i.is_pantry_staple);
                const sectionOrder = ["produce", "meat & fish", "dairy & eggs", "pantry", "spices"];
                const sections = [
                  ...sectionOrder,
                  ...[...new Set(buy.map((i) => i.section))].filter((s) => !sectionOrder.includes(s)),
                ];
                return (
                  <>
                    {sections.map((section) => {
                      const sectionItems = buy.filter((i) => i.section === section);
                      if (!sectionItems.length) return null;
                      return (
                        <div key={section}>
                          <div className="label mb-2">{section}</div>
                          <div className="space-y-2">{sectionItems.map(renderItem)}</div>
                        </div>
                      );
                    })}
                    {staples.length > 0 && (
                      <div className="opacity-75">
                        <div className="label mb-2">from your pantry — already stocked</div>
                        <div className="space-y-2">{staples.map(renderItem)}</div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            {nutrition && (
              <Block title="Per-recipe breakdown">
                <div className="grid gap-2 md:grid-cols-2">
                  {nutrition.recipes.map((recipe) => (
                    <div key={recipe.recipe_id} className="rounded-xl border border-bg4 bg-bg p-3 text-sm">
                      <div className="font-semibold">{recipe.name}</div>
                      <div className="mt-1 text-muted">{fmt(recipe.total.calories, 0)} cal · source {recipe.source}</div>
                    </div>
                  ))}
                </div>
              </Block>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function SuggestScreen({ onOpenRecipe }: { onOpenRecipe: (id: number) => void }) {
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [cuisines, setCuisines] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [includeStaples, setIncludeStaples] = useState(true);
  const [cuisine, setCuisine] = useState("");
  const [results, setResults] = useState<RecipeSuggestion[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  useEffect(() => {
    api.listIngredients({ limit: 1000 })
      .then((data) => setIngredients([...data].sort((a, b) => a.canonical_name.localeCompare(b.canonical_name))))
      .catch((err: unknown) => setNotice({ kind: "warn", text: errorText(err) }));
    api.listRecipeTags("cuisine")
      .then((tags) => setCuisines(tags.map((t) => t.tag_value)))
      .catch(() => undefined);
  }, []);

  function toggle(id: number, checked: boolean) {
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function suggest() {
    setLoading(true);
    try {
      const data = await api.suggestRecipes({
        ingredient_ids: [...selected],
        include_pantry_staples: includeStaples,
        ...(cuisine ? { cuisine } : {}),
      });
      setResults(data);
      setNotice(null);
    } catch (err) {
      setNotice({ kind: "warn", text: errorText(err) });
    } finally {
      setLoading(false);
    }
  }

  const visible = ingredients.filter((i) => i.canonical_name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="grid gap-5 xl:grid-cols-[26rem_1fr]">
      <section className="panel p-5">
        <div className="label">what do you have?</div>
        <p className="mt-2 text-sm text-muted">
          Tick what's in your kitchen — pantry staples are assumed unless you turn them off.
        </p>
        <input className="field mt-3" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter ingredients…" />
        <div className="mt-3 max-h-[44vh] space-y-1 overflow-auto pr-1">
          {visible.map((ing) => (
            <label key={ing.id} className="flex items-center gap-2 rounded-lg p-1.5 text-sm hover:bg-bg">
              <input type="checkbox" checked={selected.has(ing.id)} onChange={(e) => toggle(ing.id, e.target.checked)} />
              <span className="flex-1">{ing.canonical_name}</span>
              {ing.pantry_staple === 1 && <span className="pill text-xs">staple</span>}
            </label>
          ))}
          {!visible.length && <div className="p-2 text-sm text-muted">No matching ingredients.</div>}
        </div>
        <div className="mt-4 space-y-3 border-t border-bg4 pt-4">
          <label className="flex items-center gap-2 text-sm text-muted">
            <input type="checkbox" checked={includeStaples} onChange={(e) => setIncludeStaples(e.target.checked)} />
            count my pantry staples as available
          </label>
          <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
            <select className="field" value={cuisine} onChange={(e) => setCuisine(e.target.value)}>
              <option value="">any cuisine</option>
              {cuisines.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
            <button className="btn btn-primary" disabled={loading} onClick={() => void suggest()}>
              {loading ? "thinking…" : `suggest (${selected.size} selected)`}
            </button>
          </div>
          {cuisines.length === 0 && (
            <p className="text-xs text-muted">Tag recipes with a cuisine (edit recipe → tags) to enable the cuisine filter.</p>
          )}
        </div>
      </section>

      <section className="panel p-5">
        <NoticeBar notice={notice} />
        <div className="label">suggestions</div>
        {!results ? (
          <p className="mt-3 text-sm text-muted">Pick your ingredients and hit suggest. Searches your recipe library; web-search ideas are on the roadmap.</p>
        ) : !results.length ? (
          <p className="mt-3 text-sm text-muted">No recipes match{cuisine ? ` for ${cuisine}` : ""}. Add more ingredients or clear the cuisine filter.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {results.map((s) => (
              <div
                key={s.recipe_id}
                onClick={() => onOpenRecipe(s.recipe_id)}
                className={`cursor-pointer rounded-2xl border p-4 transition hover:border-accent/60 ${s.can_make ? "border-herb/40 bg-herb/10" : "border-bg4 bg-bg"}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-semibold text-fg">{s.name}</div>
                  {s.can_make
                    ? <span className="pill border-herb/40 text-accent2">✓ you can make this</span>
                    : <span className="pill">{s.have_count}/{s.total_required} ingredients</span>}
                </div>
                <div className="mt-1 text-sm text-muted">
                  serves {fmt(s.servings, 1)}
                  {s.prep_min != null ? ` · prep ${s.prep_min}m` : ""}
                  {s.cook_min != null ? ` · cook ${s.cook_min}m` : ""}
                </div>
                {!s.can_make && (
                  <div className="mt-2 text-sm text-warn">
                    missing: {s.missing.map((m) => m.name).join(", ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

interface CookTimer {
  id: number;
  label: string;
  secondsLeft: number;
  total: number;
}

/** Two short beeps via WebAudio — no asset needed. */
function beep() {
  try {
    const AudioCtor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AudioCtor();
    for (const startAt of [0, 0.4]) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.25, ctx.currentTime + startAt);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startAt + 0.3);
      osc.start(ctx.currentTime + startAt);
      osc.stop(ctx.currentTime + startAt + 0.3);
    }
  } catch {
    // No audio available — the visual "done" state still shows.
  }
}

function CookView({ recipeId, setRecipeId }: { recipeId: number | null; setRecipeId: (id: number) => void }) {
  const [recipes, setRecipes] = useState<RecipeListItem[]>([]);
  const [recipe, setRecipe] = useState<RecipeDetail | null>(null);
  const [idx, setIdx] = useState(0);
  const [timers, setTimers] = useState<CookTimer[]>([]);
  const timerSeq = useRef(0);

  useEffect(() => {
    api.listRecipes().then((data) => {
      setRecipes(data);
      if (!recipeId && data[0]) setRecipeId(data[0].id);
    }).catch(() => undefined);
  }, [recipeId, setRecipeId]);

  useEffect(() => {
    if (!recipeId) return;
    api.getRecipe(recipeId).then((data) => {
      setRecipe(data);
      setIdx(0);
      setTimers([]);
    }).catch(() => undefined);
  }, [recipeId]);

  // Keep the screen awake while cooking; reacquire when the tab returns.
  useEffect(() => {
    type WakeLockNavigator = Navigator & { wakeLock?: { request(type: "screen"): Promise<{ release(): Promise<void> }> } };
    const wakeLockApi = (navigator as WakeLockNavigator).wakeLock;
    if (!wakeLockApi) return;
    let sentinel: { release(): Promise<void> } | null = null;
    const acquire = () => {
      wakeLockApi.request("screen").then((s) => { sentinel = s; }).catch(() => undefined);
    };
    const onVisible = () => { if (document.visibilityState === "visible") acquire(); };
    acquire();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      void sentinel?.release().catch(() => undefined);
    };
  }, []);

  // One ticker drives every timer; finished ones beep once and stick at 0.
  useEffect(() => {
    if (!timers.some((t) => t.secondsLeft > 0)) return;
    const interval = window.setInterval(() => {
      setTimers((current) => {
        let anyFinished = false;
        const next = current.map((t) => {
          if (t.secondsLeft <= 0) return t;
          const secondsLeft = t.secondsLeft - 1;
          if (secondsLeft === 0) anyFinished = true;
          return { ...t, secondsLeft };
        });
        if (anyFinished) beep();
        return next;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, [timers]);

  function startTimer(minutes: number, label: string) {
    timerSeq.current += 1;
    setTimers((current) => [...current, { id: timerSeq.current, label, secondsLeft: minutes * 60, total: minutes * 60 }]);
  }

  function dismissTimer(id: number) {
    setTimers((current) => current.filter((t) => t.id !== id));
  }

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (!recipe) return;
      if (event.key === "ArrowRight" || event.key === " ") setIdx((n) => Math.min(recipe.steps.length - 1, n + 1));
      if (event.key === "ArrowLeft") setIdx((n) => Math.max(0, n - 1));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [recipe]);

  const step = recipe?.steps[idx];

  return (
    <section className="panel p-5">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="label">cook view</div>
          <h2 className="mt-1 text-3xl font-extrabold">{recipe?.name ?? "Choose a recipe"}</h2>
        </div>
        <select className="field w-auto min-w-72" value={recipeId ?? ""} onChange={(e) => setRecipeId(Number(e.target.value))}>
          <option value="">Choose recipe</option>
          {recipes.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
      </div>
      {recipe && step && (
        <div className="grid gap-5 xl:grid-cols-[1fr_22rem]">
          <div className="rounded-3xl border border-bg4 bg-bg p-7">
            <div className="text-sm font-bold uppercase tracking-[0.22em] text-accent">Step {idx + 1} of {recipe.steps.length}</div>
            <h3 className="mt-3 text-4xl font-extrabold">{step.title ?? "Step"}</h3>
            <p className="mt-5 whitespace-pre-line text-2xl leading-relaxed text-fg">{step.text}</p>
            {(() => {
              // Ingredients this step mentions by name — amounts at a glance.
              const stepText = step.text.toLowerCase();
              const mentioned = recipe.ingredients.filter((item) =>
                item.selected && item.ingredient_name.length > 2 && stepText.includes(item.ingredient_name.toLowerCase()));
              return mentioned.length > 0 ? (
                <div className="mt-5 flex flex-wrap gap-2">
                  {mentioned.map((item) => (
                    <span key={`${item.position}-${item.ingredient_id}`} className="pill text-base">
                      {item.quantity_text ?? `${fmt(item.quantity, 2)} ${item.unit ?? ""}`.trim()} {item.ingredient_name}
                    </span>
                  ))}
                </div>
              ) : null;
            })()}
            <div className="mt-8 flex flex-wrap items-center gap-2">
              <button className="btn" onClick={() => setIdx((n) => Math.max(0, n - 1))}>previous</button>
              <button className="btn" onClick={() => setIdx((n) => Math.min(recipe.steps.length - 1, n + 1))}>next</button>
              {step.timer_minutes != null && (
                <button
                  className="btn btn-primary"
                  onClick={() => startTimer(step.timer_minutes!, step.title ?? `Step ${idx + 1}`)}
                >
                  start {step.timer_minutes}m timer
                </button>
              )}
            </div>
            {timers.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {timers.map((t) => (
                  <span
                    key={t.id}
                    className={`pill text-lg ${t.secondsLeft === 0 ? "border-danger bg-danger/10 text-danger" : ""}`}
                  >
                    {t.label}: {t.secondsLeft === 0 ? "done!" : `${Math.floor(t.secondsLeft / 60)}:${String(t.secondsLeft % 60).padStart(2, "0")}`}
                    <button className="ml-2 hover:text-fg" title="Dismiss timer" onClick={() => dismissTimer(t.id)}>✕</button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="rounded-2xl border border-bg4 bg-bg p-4">
            <div className="label mb-3">pinned ingredients</div>
            <div className="space-y-2">
              {recipe.ingredients.filter((item) => item.selected).map((item) => (
                <div key={`${item.position}-${item.ingredient_id}`} className="text-sm text-muted">
                  <span className="text-fg">{item.ingredient_name}</span> · {item.quantity_text ?? `${fmt(item.quantity, 2)} ${item.unit ?? ""}`.trim()}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default function App() {
  const [view, setView] = useState<View>("recipes");
  const [selectedRecipeId, setSelectedRecipeId] = useState<number | null>(null);
  const [focusIngredientId, setFocusIngredientId] = useState<number | null>(null);
  const [locked, setLocked] = useState(false);
  const [keyDraft, setKeyDraft] = useState("");

  useEffect(() => {
    const onUnauthorized = () => setLocked(true);
    window.addEventListener("rl-unauthorized", onUnauthorized);
    return () => window.removeEventListener("rl-unauthorized", onUnauthorized);
  }, []);

  if (locked) {
    return (
      <div className="min-h-screen">
        <Header view={view} setView={setView} />
        <main className="mx-auto max-w-xl px-4 py-16">
          <section className="panel p-6">
            <h2 className="text-2xl font-extrabold">🔑 This library is locked</h2>
            <p className="mt-3 text-sm leading-6 text-muted">
              The API needs your key — usually because this is a new browser or its storage was
              cleared. Paste the key below; it's stored only in this browser.
            </p>
            <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_auto]">
              <input
                className="field"
                type="password"
                value={keyDraft}
                onChange={(e) => setKeyDraft(e.target.value)}
                placeholder="API key"
                autoFocus
              />
              <button
                className="btn btn-primary"
                disabled={!keyDraft.trim()}
                onClick={() => {
                  localStorage.setItem("rl_api_key", keyDraft.trim());
                  window.location.reload();
                }}
              >
                unlock
              </button>
            </div>
            <p className="mt-3 text-xs text-muted">
              On your Mac it lives at ~/.config/recipe-library/api-key.
            </p>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header view={view} setView={setView} />
      <main className="mx-auto max-w-[1500px] space-y-5 px-4 py-5 sm:px-6">
        {view === "recipes" ? (
          <RecipeScreen
            selectedId={selectedRecipeId}
            setSelectedId={setSelectedRecipeId}
            onCook={(id) => {
              setSelectedRecipeId(id);
              setView("cook");
            }}
            onOpenIngredient={(ingredientId) => {
              setFocusIngredientId(ingredientId);
              setView("ingredients");
            }}
          />
        ) : (
          <>
            {view === "add" && (
              <AddRecipeScreen
                onCreated={(id) => {
                  setSelectedRecipeId(id);
                  setView("recipes");
                }}
              />
            )}
            {view === "ingredients" && <IngredientLibraryScreen focusIngredientId={focusIngredientId} />}
            {view === "plan" && <MealPlanScreen onListCreated={() => setView("shopping")} />}
            {view === "shopping" && <ShoppingListScreen />}
            {view === "suggest" && (
              <SuggestScreen
                onOpenRecipe={(id) => {
                  setSelectedRecipeId(id);
                  setView("recipes");
                }}
              />
            )}
            {view === "cook" && <CookView recipeId={selectedRecipeId} setRecipeId={setSelectedRecipeId} />}
          </>
        )}
      </main>
    </div>
  );
}
